import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router";
import { useTranslation } from "react-i18next";
import { Document, Page, pdfjs } from "react-pdf";
import { Parse, initParse } from "../lib/parse";
import { getSessionToken } from "../lib/auth";
import i18n from "../i18n";
import Card from "../components/ui/Card";
import Button from "../components/ui/Button";
import Icon from "../components/ui/Icon";
import Alert from "../components/ui/Alert";
import Modal from "../components/ui/Modal";
import ConfirmDialog from "../components/ui/ConfirmDialog";
import InteractiveWidget from "../components/signing/InteractiveWidget";
import SignatureModal from "../components/signing/SignatureModal";
import AgreementSign from "../components/signing/AgreementSign";
import VerificationModal from "../components/verification/VerificationModal";
import { resolveRequireDocumentBack } from "../components/verification/verificationUtils";
import useWidgetState from "../hooks/useWidgetState";

pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ""}/pdf.worker.min.mjs`;

const MIN_ZOOM = 0.6;
const MAX_ZOOM = 2;
const ZOOM_STEP = 0.1;
const DEFAULT_INITIAL_ZOOM = 0.82;
const DEFAULT_SIGNATURE_VALIDATION = {
  minArea: 1500,
  minLength: 180
};
const RECIPIENT_TOUR_TOOLTIP_WIDTH = 360;
const RECIPIENT_TOUR_HIDE_KEY = "danasign_recipient_tour_hide";
const RECIPIENT_TOUR_SEEN_KEY = "danasign_recipient_tour_seen";

const normalizeValue = (value) => String(value || "").trim();
const normalizeEmail = (value) => String(value || "").trim().toLowerCase();
const hasAuthenticatedSignerSession = () =>
  Boolean(normalizeValue(getSessionToken()) && Parse.User.current());

const signerContextStorageKey = (docId) => `danasign.signer-context:${docId || ""}`;

const readSignerContext = (docId) => {
  if (!docId || typeof window === "undefined" || !window.sessionStorage) {
    return {};
  }
  try {
    const raw = window.sessionStorage.getItem(signerContextStorageKey(docId));
    return raw ? JSON.parse(raw) : {};
  } catch (error) {
    return {};
  }
};

const persistSignerContext = (docId, context = {}) => {
  if (!docId || typeof window === "undefined" || !window.sessionStorage) {
    return;
  }
  try {
    const current = readSignerContext(docId);
    const next = {
      ...current,
      ...context
    };
    window.sessionStorage.setItem(signerContextStorageKey(docId), JSON.stringify(next));
  } catch (error) {
    // noop
  }
};

const getParseAwareValue = (entity, key) => {
  if (!entity || typeof entity !== "object") {
    return undefined;
  }
  if (Object.prototype.hasOwnProperty.call(entity, key) && entity[key] !== undefined) {
    return entity[key];
  }
  if (typeof entity.get === "function") {
    const getterValue = entity.get(key);
    if (getterValue !== undefined) {
      return getterValue;
    }
  }
  return entity[key];
};

const getPointerObjectId = (pointer = {}) =>
  normalizeValue(
    getParseAwareValue(pointer, "objectId") ||
      pointer?.id ||
      getParseAwareValue(pointer, "Id") ||
      getParseAwareValue(pointer, "id")
  );

const getSignerObjectId = (signer = {}) =>
  normalizeValue(
    getParseAwareValue(signer, "objectId") ||
      signer?.id ||
      getParseAwareValue(signer, "Id") ||
      getParseAwareValue(signer, "signerObjId") ||
      getPointerObjectId(getParseAwareValue(signer, "signerPtr"))
  );

const getSignerUserObjectId = (signer = {}) => {
  const userPtr =
    getParseAwareValue(signer, "UserId") ||
    getParseAwareValue(signer, "userId") ||
    getParseAwareValue(signer, "User") ||
    getParseAwareValue(signer, "UserPtr");
  return normalizeValue(getPointerObjectId(userPtr) || getParseAwareValue(signer, "Id"));
};

const getSignerEmail = (signer = {}) =>
  normalizeEmail(
    getParseAwareValue(signer, "Email") ||
      getParseAwareValue(signer, "email") ||
      getParseAwareValue(getParseAwareValue(signer, "UserId"), "Email") ||
      getParseAwareValue(getParseAwareValue(signer, "User"), "email")
  );

const normalizePageNumber = (value) => {
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) ? parsed : 1;
};

const parseThreshold = (value, fallback) => {
  const parsedValue = Number.parseInt(value, 10);
  return Number.isNaN(parsedValue) ? fallback : parsedValue;
};

const getImageSize = (src) =>
  new Promise((resolve, reject) => {
    if (!src) {
      resolve({ width: 0, height: 0 });
      return;
    }
    const image = new Image();
    image.onload = () =>
      resolve({
        width: image.naturalWidth || image.width || 0,
        height: image.naturalHeight || image.height || 0
      });
    image.onerror = () => reject(new Error("image-load-failed"));
    image.src = src;
  });

const validateImageSignature = (naturalWH, minAreaThreshold, minLengthThreshold) => {
  const area = (naturalWH?.width || 0) * (naturalWH?.height || 0);
  const longestSide = Math.max(naturalWH?.width || 0, naturalWH?.height || 0);
  return area >= minAreaThreshold && longestSide >= minLengthThreshold;
};

const extractSignatureThresholdsFromDocument = (documentData = {}) => {
  const extUser = documentData?.ExtUserPtr;
  const tenant = getParseAwareValue(extUser, "TenantId");
  const minAreaRaw =
    getParseAwareValue(tenant, "MinSignatureArea") ??
    getParseAwareValue(extUser, "MinSignatureArea");
  const minLengthRaw =
    getParseAwareValue(tenant, "MinSignatureLength") ??
    getParseAwareValue(extUser, "MinSignatureLength");

  return {
    minArea: parseThreshold(minAreaRaw, DEFAULT_SIGNATURE_VALIDATION.minArea),
    minLength: parseThreshold(minLengthRaw, DEFAULT_SIGNATURE_VALIDATION.minLength)
  };
};

const isPrefillWidget = (widget = {}, page = {}) => {
  const widgetType = String(widget?.type || "").trim().toLowerCase();
  const role = String(widget?.Role || widget?.role || page?.Role || page?.role || "")
    .trim()
    .toLowerCase();
  return (
    widgetType === "text" &&
    (widget?.isPrefill === true || widget?.options?.isPrefill === true || role === "prefill")
  );
};

const collectSignerIdCandidates = (contactId, signer = {}) => {
  const normalizedContact = normalizeValue(contactId);
  const ids = [
    normalizedContact.includes("@") ? "" : normalizedContact,
    getSignerObjectId(signer),
    getParseAwareValue(signer, "signerObjId"),
    getPointerObjectId(getParseAwareValue(signer, "signerPtr")),
    getSignerUserObjectId(signer),
    getPointerObjectId(getParseAwareValue(signer, "UserPtr"))
  ]
    .map((value) => normalizeValue(value))
    .filter(Boolean);
  return Array.from(new Set(ids));
};

const collectSignerEmailCandidates = (contactId, signer = {}, fallbackEmail = "") => {
  const normalizedContact = normalizeValue(contactId);
  const emails = [
    normalizedContact?.includes?.("@") ? normalizedContact : "",
    fallbackEmail,
    getSignerEmail(signer),
    getParseAwareValue(getParseAwareValue(signer, "options"), "recipientEmail")
  ]
    .map((value) => normalizeEmail(value))
    .filter(Boolean);
  return Array.from(new Set(emails));
};

const widgetHasSignerMatch = (widget = {}, holder = {}, signerIds = [], signerEmails = []) => {
  const legacyHolderSignerId = Array.isArray(holder?.placeHolder)
    ? normalizeValue(holder?.Id || holder?.id)
    : "";
  const widgetIds = [
    widget?.options?.recipientId,
    widget?.recipientId,
    widget?.signerObjId,
    widget?.options?.signerObjId,
    widget?.options?.contactBookId,
    widget?.contactBookId,
    legacyHolderSignerId,
    holder?.signerObjId,
    holder?.options?.signerObjId,
    holder?.contactBookId,
    holder?.signerPtr?.objectId
  ]
    .map((value) => normalizeValue(value))
    .filter(Boolean);

  const widgetEmails = [
    widget?.options?.recipientEmail,
    widget?.options?.email,
    widget?.options?.signerEmail,
    widget?.Email,
    widget?.email,
    holder?.email,
    holder?.Email
  ]
    .map((value) => normalizeEmail(value))
    .filter(Boolean);

  // If the widget has explicit signer identifiers, require a positive match.
  // If it has identifiers that do NOT match, reject even if there is a
  // partial match on a different field (prevents cross-signer editing).
  const hasAnyId = widgetIds.length > 0;
  const hasAnyEmail = widgetEmails.length > 0;

  if (!hasAnyId && !hasAnyEmail) {
    // Widget has no signer identifiers at all — cannot determine ownership.
    return false;
  }

  const idMatch = hasAnyId && widgetIds.some((value) => signerIds.includes(value));
  const emailMatch = hasAnyEmail && widgetEmails.some((value) => signerEmails.includes(value));

  // If the widget has an explicit email that does NOT match the current
  // signer, reject it regardless of any ID match.  This prevents the
  // scenario where a stale or shared ID accidentally grants edit access
  // to another signer's widgets.
  if (hasAnyEmail && !emailMatch) {
    return false;
  }

  return idMatch || emailMatch;
};

const toWidgetHolderMeta = (holder = {}) => ({
  signerObjId: normalizeValue(holder?.signerObjId || holder?.options?.signerObjId || ""),
  contactBookId: normalizeValue(holder?.contactBookId || ""),
  email: normalizeEmail(holder?.email || holder?.Email || ""),
  Role: holder?.Role || holder?.role || "",
  IdentityVerification: holder?.IdentityVerification
});

const decorateWidgetWithHolderMeta = (widget = {}, holder = {}) => ({
  ...widget,
  ...(widget?.IdentityVerification || holder?.IdentityVerification
    ? {
        IdentityVerification: widget?.IdentityVerification || holder?.IdentityVerification
      }
    : {}),
  ...(widget?.Role || holder?.Role ? { Role: widget?.Role || holder?.Role } : {}),
  __holderMeta: toWidgetHolderMeta(holder)
});

const hasWidgetResponse = (widget = {}) => {
  if (!widget?.options || !Object.prototype.hasOwnProperty.call(widget.options, "response")) {
    return false;
  }
  const response = widget.options.response;
  if (response === null || response === undefined) {
    return false;
  }
  if (typeof response === "string") {
    return response.trim() !== "";
  }
  if (typeof response === "boolean") {
    return response;
  }
  return true;
};

const buildSignerPages = (allPlaceholders = [], signerIds = [], signerEmails = []) => {
  const pageMap = new Map();
  const pushWidget = (pageNumberInput, widget, holder = {}) => {
    const pageNumber = normalizePageNumber(pageNumberInput);
    const includePrefill = isPrefillWidget(widget, holder);
    if (!includePrefill && !widgetHasSignerMatch(widget, holder, signerIds, signerEmails)) {
      return;
    }
    const baseWidget = decorateWidgetWithHolderMeta(widget, holder);
    if (!pageMap.has(pageNumber)) {
      pageMap.set(pageNumber, []);
    }
    pageMap.get(pageNumber).push(baseWidget);
  };

  allPlaceholders.forEach((entry) => {
    // New UI format: [{ pageNumber, pos }]
    if (Array.isArray(entry?.pos)) {
      const pageNumber = normalizePageNumber(entry?.pageNumber);
      entry.pos.forEach((widget) => pushWidget(pageNumber, widget, entry));
      return;
    }
    // Legacy/API format: [{ Role, signerObjId, placeHolder: [{ pageNumber, pos }] }]
    if (Array.isArray(entry?.placeHolder)) {
      entry.placeHolder.forEach((pageEntry) => {
        const pageNumber = normalizePageNumber(pageEntry?.pageNumber);
        (pageEntry?.pos || []).forEach((widget) => pushWidget(pageNumber, widget, entry));
      });
    }
  });

  return Array.from(pageMap.entries())
    .sort((a, b) => a[0] - b[0])
    .map(([pageNumber, pos]) => ({ pageNumber, pos }));
};

const buildOtherCompletedPages = (allPlaceholders = [], signerIds = [], signerEmails = []) => {
  const pageMap = new Map();
  const pushWidget = (pageNumberInput, widget, holder = {}) => {
    const pageNumber = normalizePageNumber(pageNumberInput);
    const isOwnedByCurrentSigner = widgetHasSignerMatch(widget, holder, signerIds, signerEmails);
    if (isOwnedByCurrentSigner || isPrefillWidget(widget, holder) || !hasWidgetResponse(widget)) {
      return;
    }
    const baseWidget = decorateWidgetWithHolderMeta(widget, holder);
    if (!pageMap.has(pageNumber)) {
      pageMap.set(pageNumber, []);
    }
    pageMap.get(pageNumber).push(baseWidget);
  };

  allPlaceholders.forEach((entry) => {
    if (Array.isArray(entry?.pos)) {
      const pageNumber = normalizePageNumber(entry?.pageNumber);
      entry.pos.forEach((widget) => pushWidget(pageNumber, widget, entry));
      return;
    }
    if (Array.isArray(entry?.placeHolder)) {
      entry.placeHolder.forEach((pageEntry) => {
        const pageNumber = normalizePageNumber(pageEntry?.pageNumber);
        (pageEntry?.pos || []).forEach((widget) => pushWidget(pageNumber, widget, entry));
      });
    }
  });

  return Array.from(pageMap.entries())
    .sort((a, b) => a[0] - b[0])
    .map(([pageNumber, pos]) => ({ pageNumber, pos }));
};

const parseWidgetId = (widgetId) => {
  const [pageNumRaw, widgetIdxRaw] = String(widgetId || "").split("-");
  const pageNumber = Number.parseInt(pageNumRaw, 10);
  const widgetIndex = Number.parseInt(widgetIdxRaw, 10);
  if (!Number.isFinite(pageNumber) || !Number.isFinite(widgetIndex)) {
    return null;
  }
  return { pageNumber, widgetIndex };
};

const resolveCheckboxLabel = (widget = {}, t) => {
  const firstOptionValue =
    Array.isArray(widget?.options?.values) && widget.options.values.length
      ? widget.options.values[0]
      : "";
  return String(
    widget?.options?.label ||
      widget?.textValue ||
      firstOptionValue ||
      widget?.options?.defaultValue ||
      widget?.options?.name ||
      widget?.label ||
      t("checkbox-label", { defaultValue: "I agree" })
  ).trim();
};

const parseCheckboxValue = (value) => {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  if (Array.isArray(value)) {
    if (!value.length) return false;
    return value.some((item) => parseCheckboxValue(item));
  }
  const normalized = String(value || "").trim().toLowerCase();
  if (!normalized) return false;
  if (["0", "false", "no", "off", "unchecked"].includes(normalized)) return false;
  return true;
};

const normalizeBooleanFlag = (value) => {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  const normalized = String(value || "").trim().toLowerCase();
  if (!normalized) return false;
  return ["1", "true", "yes", "on", "required"].includes(normalized);
};

const isWidgetRequired = (widget = {}) => {
  const status = String(widget?.options?.status || "").trim().toLowerCase();
  if (status === "required") return true;
  if (status === "optional") return false;
  return (
    normalizeBooleanFlag(widget?.required) ||
    normalizeBooleanFlag(widget?.options?.required) ||
    normalizeBooleanFlag(widget?.options?.validation?.required)
  );
};

function ExternalSignerPage() {
  const { t } = useTranslation();
  const { docId, contactBookId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const sendmail = searchParams.get("sendmail");
  const storedSignerContext = readSignerContext(docId);
  const storedContactBookId = normalizeValue(storedSignerContext?.contactBookId);
  const storedSignerEmail = normalizeEmail(storedSignerContext?.email);
  const contactBookIdQuery = normalizeValue(searchParams.get("contactBookId"));
  const signerEmailQuery = normalizeEmail(searchParams.get("email"));
  const effectiveContactBookId = normalizeValue(
    contactBookId || storedContactBookId || contactBookIdQuery
  );
  const effectiveContactEmail =
    signerEmailQuery ||
    storedSignerEmail ||
    (effectiveContactBookId.includes("@") ? normalizeEmail(effectiveContactBookId) : "");

  useEffect(() => {
    const hasActiveSession = Boolean(getSessionToken());
    const currentPath = String(location?.pathname || "");
    const isRecipientPath = currentPath.includes("/recipientSignPdf/");
    const isLoadRecipientPath = currentPath.includes("/load/recipientSignPdf/");
    if (hasActiveSession && docId && isRecipientPath && !isLoadRecipientPath) {
      persistSignerContext(docId, {
        contactBookId: normalizeValue(contactBookId) || storedContactBookId || "",
        email:
          normalizeEmail(signerEmailQuery || (normalizeValue(contactBookId).includes("@") ? contactBookId : "")) ||
          storedSignerEmail ||
          ""
      });
      const query = new URLSearchParams();
      if (sendmail === "false") {
        query.set("sendmail", "false");
      }
      const queryString = query.toString();
      navigate(`/pdfRequestFiles/${docId}${queryString ? `?${queryString}` : ""}`, { replace: true });
    }
  }, [contactBookId, docId, location?.pathname, navigate, sendmail, signerEmailQuery, storedContactBookId, storedSignerEmail]);

  // Document state
  const [pdfUrl, setPdfUrl] = useState("");
  const [pdfFile, setPdfFile] = useState(null);
  const [signedDownloadUrl, setSignedDownloadUrl] = useState("");
  const [certificateDownloadUrl, setCertificateDownloadUrl] = useState("");
  const [documentName, setDocumentName] = useState("");
  const [numPages, setNumPages] = useState(0);
  const [currentPageNumber, setCurrentPageNumber] = useState(1);
  const [zoomScale, setZoomScale] = useState(() => {
    if (typeof window !== 'undefined' && window.innerWidth < 768) {
      return 1; // Mobile: no extra zoom, PDF fits to container width
    }
    return DEFAULT_INITIAL_ZOOM;
  });
  const [pageWidth, setPageWidth] = useState(980);
  const [pageOriginalWidths, setPageOriginalWidths] = useState({});
  const [pageOriginalHeights, setPageOriginalHeights] = useState({});

  // Signer state
  const [signerEmail, setSignerEmail] = useState("");
  const [signerName, setSignerName] = useState("");
  const [signerObjectId, setSignerObjectId] = useState("");
  const [signerUserObjectId, setSignerUserObjectId] = useState("");
  const [placeholders, setPlaceholders] = useState([]);
  const [myPlaceholders, setMyPlaceholders] = useState([]);
  const [otherCompletedPlaceholders, setOtherCompletedPlaceholders] = useState([]);
  const [signerIdCandidatesState, setSignerIdCandidatesState] = useState([]);
  const [signerEmailCandidatesState, setSignerEmailCandidatesState] = useState([]);
  const [tenantLogo, setTenantLogo] = useState("");
  const [redirectUrl, setRedirectUrl] = useState("");
  const [signedDate, setSignedDate] = useState(null);
  
  // Biometric verification state
  const [verificationRequired, setVerificationRequired] = useState(false);
  const [verificationComplete, setVerificationComplete] = useState(false);
  const [verificationConfig, setVerificationConfig] = useState({ threshold: 85, requireDocument: true });
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [verificationEvidence, setVerificationEvidence] = useState(null);

  // UI state
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [isCompleted, setIsCompleted] = useState(false);
  const [isExpired, setIsExpired] = useState(false);
  const [isDeclined, setIsDeclined] = useState(false);
  const [alreadySigned, setAlreadySigned] = useState(false);
  const [showSuccessScreen, setShowSuccessScreen] = useState(false);
  const [signingResult, setSigningResult] = useState({
    isCompleted: false,
    signedCount: 0,
    totalSigners: 0
  });
  const [signatureValidation, setSignatureValidation] = useState(DEFAULT_SIGNATURE_VALIDATION);
  const [isAgreementAccepted, setIsAgreementAccepted] = useState(() =>
    hasAuthenticatedSignerSession()
  );

  // Widget interaction state
  const [isSignatureModalOpen, setIsSignatureModalOpen] = useState(false);
  const [applyInitialsToAllWidgets, setApplyInitialsToAllWidgets] = useState(false);
  const [activeWidgetId, setActiveWidgetId] = useState(null);
  const [activeWidgetType, setActiveWidgetType] = useState('signature');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showDeclineConfirmDialog, setShowDeclineConfirmDialog] = useState(false);
  const [declineReason, setDeclineReason] = useState("");
  const [isPreparingCertificate, setIsPreparingCertificate] = useState(false);
  
  // Text/Input widget modal state
  const [isTextModalOpen, setIsTextModalOpen] = useState(false);
  const [textInputValue, setTextInputValue] = useState('');
  
  // Checkbox widget state
  const [isCheckboxModalOpen, setIsCheckboxModalOpen] = useState(false);
  const [checkboxValue, setCheckboxValue] = useState(false);
  const [checkboxLabel, setCheckboxLabel] = useState('');
  
  // Date widget state
  const [isDateModalOpen, setIsDateModalOpen] = useState(false);
  const [dateValue, setDateValue] = useState('');
  
  // Image widget state
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [isImageLoading, setIsImageLoading] = useState(false);
  
  // Alert state
  const [alert, setAlert] = useState({ show: false, type: 'error', message: '' });

  // Mobile sidebar state
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  // Recipient tour state
  const [showRecipientTour, setShowRecipientTour] = useState(false);
  const [recipientTourStep, setRecipientTourStep] = useState(0);
  const [recipientTourTargetRect, setRecipientTourTargetRect] = useState(null);
  const [isTourEnabled, setIsTourEnabled] = useState(false);

  // Initialize widget state management
  const {
    widgetState,
    updateWidget,
    getWidget,
    isAllRequiredFilled,
    getProgress,
    initializeFromPlaceholders,
    clearState
  } = useWidgetState(docId, placeholders);

  const containerRef = useRef(null);

  // Responsive pageWidth: adapt to container/viewport on mount and resize
  useEffect(() => {
    const updatePageWidth = () => {
      const container = containerRef.current;
      if (container) {
        // Use container width minus padding (8px each side on mobile, 16px on md+)
        const available = container.clientWidth - 16;
        setPageWidth(Math.min(980, Math.max(300, available)));
      } else {
        // Fallback: use viewport width minus sidebar (on md+) and padding
        const vw = window.innerWidth;
        const sidebarWidth = vw >= 768 ? 320 : 0;
        const available = vw - sidebarWidth - 32;
        setPageWidth(Math.min(980, Math.max(300, available)));
      }
    };
    // Small delay to let layout settle after sidebar toggle
    const timer = setTimeout(updatePageWidth, 50);
    window.addEventListener('resize', updatePageWidth);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', updatePageWidth);
    };
  }, [isSidebarOpen]);

  const canCurrentSignerEditWidget = (widget = {}, holder = {}) => {
    if (isPrefillWidget(widget, holder)) {
      return false;
    }
    if (!signerIdCandidatesState.length && !signerEmailCandidatesState.length) {
      return false;
    }
    return widgetHasSignerMatch(widget, holder, signerIdCandidatesState, signerEmailCandidatesState);
  };

  const normalizeDocumentPayload = (response) => {
    if (!response) return null;
    if (response?.error) return null;
    if (response?.result?.error) return null;
    return response?.result || response;
  };

  const resolveSecureUrl = async (rawUrl) => {
    if (!rawUrl) return "";
    try {
      const signedUrl = await Parse.Cloud.run("getsignedurl", {
        url: rawUrl,
        ...(docId ? { docId } : {})
      });
      if (typeof signedUrl === "string" && signedUrl.trim()) {
        return signedUrl;
      }
    } catch (error) {
      // fallback to raw URL
    }
    return rawUrl;
  };

  const resolveDownloadAssets = async (docPayload, { ensureCertificate = false } = {}) => {
    if (!docPayload) {
      return { signedUrl: "", certificateUrl: "" };
    }

    const signedSourceUrl = String(docPayload?.SignedUrl || docPayload?.URL || "").trim();
    let certificateSourceUrl = String(docPayload?.CertificateUrl || "").trim();

    if (ensureCertificate && docPayload?.IsCompleted && !certificateSourceUrl && docId) {
      try {
        const certificateRes = await Parse.Cloud.run("generatecertificate", { docId });
        certificateSourceUrl = String(certificateRes?.CertificateUrl || "").trim();
      } catch (error) {
        console.warn("Unable to generate certificate on demand", error);
      }
    }

    const [signedUrl, certificateUrl] = await Promise.all([
      resolveSecureUrl(signedSourceUrl),
      resolveSecureUrl(certificateSourceUrl)
    ]);

    return {
      signedUrl: signedUrl || signedSourceUrl,
      certificateUrl: certificateUrl || certificateSourceUrl
    };
  };

  const triggerDownload = (url) => {
    if (!url) return;
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.target = "_blank";
    anchor.rel = "noopener noreferrer";
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  };

  const handleDownloadSigned = () => {
    if (!isCompleted) {
      setAlert({
        show: true,
        type: "warning",
        message: t("verification.download-available-on-completion", {
          defaultValue: "El documento firmado estará disponible cuando todos los firmantes hayan completado la firma."
        })
      });
      return;
    }
    if (!signedDownloadUrl) {
      setAlert({
        show: true,
        type: "warning",
        message: t("new-ui.individual-signing.pdf-missing", {
          defaultValue: "No signed document is available for download yet."
        })
      });
      return;
    }
    triggerDownload(signedDownloadUrl);
  };

  const handleDownloadCertificate = async () => {
    if (!isCompleted) {
      setAlert({
        show: true,
        type: "warning",
        message: t("verification.download-available-on-completion", {
          defaultValue: "El certificado estará disponible cuando todos los firmantes hayan completado la firma."
        })
      });
      return;
    }
    const shouldForceRegenerate = Boolean(docId && isCompleted);

    if (certificateDownloadUrl && !shouldForceRegenerate) {
      triggerDownload(certificateDownloadUrl);
      return;
    }

    if (!docId) {
      return;
    }

    setIsPreparingCertificate(true);
    try {
      const certificateRes = await Parse.Cloud.run("generatecertificate", {
        docId,
        ...(shouldForceRegenerate ? { force: true } : {})
      });
      const generatedUrl = String(certificateRes?.CertificateUrl || "").trim();
      const nextUrl = await resolveSecureUrl(generatedUrl || certificateDownloadUrl);

      if (!nextUrl) {
        throw new Error(
          t("generate-certificate-err", {
            defaultValue:
              "We are currently experiencing an issue with certificate generation. Please try again later."
          })
        );
      }

      setCertificateDownloadUrl(nextUrl);
      triggerDownload(nextUrl);
    } catch (error) {
      setAlert({
        show: true,
        type: "error",
        message:
          error?.message ||
          t("generate-certificate-err", {
            defaultValue:
              "We are currently experiencing an issue with certificate generation. Please try again later."
          })
      });
    } finally {
      setIsPreparingCertificate(false);
    }
  };

  // Initialize Parse on mount
  useEffect(() => {
    initParse();
  }, []);

  // Load document on mount
  useEffect(() => {
    loadDocument();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docId, effectiveContactBookId, effectiveContactEmail]);

  const loadDocument = async () => {
    setIsLoading(true);
    setError("");
    setIsAgreementAccepted(hasAuthenticatedSignerSession());
    setOtherCompletedPlaceholders([]);
    setSignerIdCandidatesState([]);
    setSignerEmailCandidatesState([]);
    setSignerUserObjectId("");

    console.log("=== LOAD DOCUMENT START ===");
    console.log("docId:", docId);
    console.log("contactBookId:", effectiveContactBookId);
    console.log("signerEmailFromQuery:", effectiveContactEmail);

    try {
      const sessionToken = normalizeValue(getSessionToken());
      // Get document details using Parse Cloud function
      let documentData = await Parse.Cloud.run("getDocument", {
        docId: docId
      });
      if (documentData?.error === "otp_required" && sessionToken) {
        try {
          documentData = await Parse.Cloud.run(
            "getDocument",
            { docId: docId },
            { sessionToken }
          );
        } catch (retryError) {
          // keep original response and continue OTP fallback below
        }
      }

      console.log("=== DOCUMENT DATA RECEIVED ===");
      console.log("documentData:", documentData);

      if (!documentData || documentData.error) {
        if (documentData?.error === "otp_required") {
          const currentParseUser = Parse.User.current();
          const currentUserEmail = normalizeEmail(
            currentParseUser?.get?.("email") ||
              currentParseUser?.get?.("Email") ||
              currentParseUser?.attributes?.email ||
              currentParseUser?.attributes?.Email ||
              currentParseUser?.email ||
              localStorage.getItem("userEmail") ||
              ""
          );
          const signerEmailForOtp = normalizeEmail(
            effectiveContactEmail || signerEmailQuery || storedSignerEmail || ""
          );
          const signerContactForOtp = normalizeValue(
            contactBookId || effectiveContactBookId || storedContactBookId || ""
          );
          const shouldRouteToOtpLogin =
            !sessionToken ||
            !currentParseUser ||
            (signerEmailForOtp && (!currentUserEmail || signerEmailForOtp !== currentUserEmail));

          if (shouldRouteToOtpLogin && docId && signerEmailForOtp) {
            const payload = btoa(
              `${docId}/${signerEmailForOtp}/${signerContactForOtp}/${sendmail === "false" ? "false" : ""}`
            );
            navigate(`/login/${payload}`, { replace: true });
            return;
          }
          setError(t("otp-required", { defaultValue: "OTP verification required" }));
        } else if (documentData?.error === "document_archived") {
          setError(
            t("document-archived-unavailable", {
              defaultValue:
                "Este documento fue archivado y ya no está disponible. Comuníquese con el emisor del documento."
            })
          );
        } else {
          setError(t("document-not-found", { defaultValue: "Document not found" }));
        }
        return;
      }

      // Set document language if available
      const docLanguage = documentData.Language || documentData.ExtUserPtr?.Language || 'es';
      console.log("Document language:", docLanguage);
      if (docLanguage && i18n.language !== docLanguage) {
        i18n.changeLanguage(docLanguage);
      }
      setIsTourEnabled(documentData?.IsTourEnabled !== false);

      // Check document status
      if (documentData.IsCompleted) {
        setIsCompleted(true);
        setAlreadySigned(true);
      }

      if (documentData.IsDeclined) {
        setIsDeclined(true);
      }

      // Check expiry
      const expireDate = new Date(documentData.ExpiryDate?.iso);
      const now = new Date();
      if (now > expireDate) {
        setIsExpired(true);
      }

      // Set document details
      setDocumentName(documentData.Name || "");
      const { signedUrl, certificateUrl } = await resolveDownloadAssets(documentData, {
        ensureCertificate: Boolean(documentData.IsCompleted)
      });
      setSignedDownloadUrl(signedUrl || "");
      setCertificateDownloadUrl(certificateUrl || "");
      const viewerUrl = signedUrl || documentData.URL || "";
      setPdfUrl(viewerUrl);
      setPdfFile(viewerUrl);
      setSignatureValidation(extractSignatureThresholdsFromDocument(documentData));

      // Get redirect URL if configured
      const docRedirectUrl = documentData.RedirectUrl || "";
      setRedirectUrl(docRedirectUrl);

      // Get tenant logo - Parse objects need .get() method
      let tenantLogoUrl = "";
      
      // First try ExtUserPtr.TenantId
      if (documentData.ExtUserPtr) {
        const extUserTenantId = documentData.ExtUserPtr.get ? documentData.ExtUserPtr.get('TenantId') : documentData.ExtUserPtr.TenantId;
        if (extUserTenantId) {
          tenantLogoUrl = extUserTenantId.get ? (extUserTenantId.get('Logo') || extUserTenantId.get('logo')) : (extUserTenantId.Logo || extUserTenantId.logo);
        }
      }
      
      // If not found in ExtUserPtr, try Signers array (Parse objects)
      if (!tenantLogoUrl && documentData.Signers && documentData.Signers.length > 0) {
        const firstSigner = documentData.Signers[0];
        const tenantId = firstSigner.get ? firstSigner.get('TenantId') : firstSigner.TenantId;
        
        if (tenantId) {
          tenantLogoUrl = tenantId.get ? (tenantId.get('Logo') || tenantId.get('logo')) : (tenantId.Logo || tenantId.logo);
        }
      }
      
      console.log("Tenant logo URL:", tenantLogoUrl);
      setTenantLogo(tenantLogoUrl || "");

      // Find current signer - Try multiple strategies
      console.log("=== SIGNER DEBUG ===");
      console.log("documentData.Signers:", JSON.stringify(documentData.Signers, null, 2));
      console.log("contactBookId:", effectiveContactBookId);

      const currentParseUser = Parse.User.current();
      const loggedUserId = normalizeValue(currentParseUser?.id || currentParseUser?.objectId);
      const loggedUserEmail = normalizeEmail(
        currentParseUser?.get?.("email") ||
          currentParseUser?.get?.("Email") ||
          currentParseUser?.attributes?.email ||
          currentParseUser?.attributes?.Email ||
          currentParseUser?.email ||
          localStorage.getItem("userEmail") ||
          ""
      );
      const allSigners = Array.isArray(documentData.Signers) ? documentData.Signers : [];
      const linkEmailCandidate =
        effectiveContactEmail ||
        (effectiveContactBookId.includes("@") ? normalizeEmail(effectiveContactBookId) : "");
      
      let currentSigner = null;
      
      // Strategy 1: Direct match on objectId or Id
      if (effectiveContactBookId && !effectiveContactBookId.includes("@")) {
        currentSigner = allSigners.find((signer) => {
          const signerId = getSignerObjectId(signer);
          return signerId && signerId === effectiveContactBookId;
        });
      }
      
      // Strategy 3: Check email match if contactBookId looks like an email
      if (!currentSigner && linkEmailCandidate) {
        currentSigner = allSigners.find((signer) => {
          const signerMail = getSignerEmail(signer);
          return signerMail && signerMail === linkEmailCandidate;
        });
      }

      // Strategy 4: Match authenticated user by UserId/objectId
      if (!currentSigner && loggedUserId) {
        currentSigner = allSigners.find((signer) => {
          const signerUserId = getSignerUserObjectId(signer);
          const signerId = getSignerObjectId(signer);
          return (
            (signerUserId && signerUserId === loggedUserId) ||
            (signerId && signerId === loggedUserId)
          );
        });
      }

      // Strategy 5: Match authenticated user by email
      if (!currentSigner && loggedUserEmail) {
        currentSigner = allSigners.find((signer) => {
          const signerMail = getSignerEmail(signer);
          return signerMail && signerMail === loggedUserEmail;
        });
      }
      
      console.log("currentSigner found:", currentSigner);

      if (currentSigner) {
        const resolvedSignerId =
          getSignerObjectId(currentSigner) || loggedUserId || effectiveContactBookId || "";
        const resolvedSignerEmail =
          getSignerEmail(currentSigner) || loggedUserEmail || linkEmailCandidate || "";

        setSignerName(
          getParseAwareValue(currentSigner, "Name") ||
            getParseAwareValue(currentSigner, "name") ||
            currentParseUser?.get?.("name") ||
            currentParseUser?.get?.("username") ||
            ""
        );
        setSignerEmail(resolvedSignerEmail);
        setSignerObjectId(resolvedSignerId);
        setSignerUserObjectId(getSignerUserObjectId(currentSigner) || loggedUserId || "");
        persistSignerContext(docId, {
          contactBookId: resolvedSignerId,
          email: resolvedSignerEmail
        });
      } else {
        console.log("No signer found matching contactBookId");
      }
      const signerIdCandidates = Array.from(
        new Set(
          [...collectSignerIdCandidates(effectiveContactBookId, currentSigner || {}), loggedUserId].filter(
            Boolean
          )
        )
      );
      const signerEmailCandidates = Array.from(
        new Set(
          [
            ...collectSignerEmailCandidates(
              effectiveContactBookId,
              currentSigner || {},
              effectiveContactEmail
            ),
            loggedUserEmail
          ].filter(Boolean)
        )
      );
      setSignerIdCandidatesState(signerIdCandidates);
      setSignerEmailCandidatesState(signerEmailCandidates);

      // Check if already signed
      const auditTrail = documentData.AuditTrail || [];
      const signedEntry = auditTrail.find(
        (entry) => {
          if (String(entry?.Activity || "").toLowerCase() !== "signed") {
            return false;
          }
          const userId = normalizeValue(entry?.UserPtr?.objectId);
          const userEmail = normalizeEmail(
            entry?.UserPtr?.Email || entry?.Email || entry?.email || ""
          );
          return signerIdCandidates.includes(userId) || signerEmailCandidates.includes(userEmail);
        }
      );

      if (signedEntry) {
        setAlreadySigned(true);
        // Capture the signed date
        if (signedEntry.createdAt) {
          setSignedDate(new Date(signedEntry.createdAt));
        }
      }

      // Get placeholders for current signer
      console.log("=== PLACEHOLDERS DEBUG ===");
      const allPlaceholders = documentData.Placeholders || [];
      console.log("allPlaceholders structure:", allPlaceholders);
      console.log("contactBookId:", effectiveContactBookId);
      
      // Support both New UI placeholders ([{pageNumber,pos}]) and
      // API/legacy placeholders ([{Role, signerObjId, placeHolder:[{pageNumber,pos}]}]).
      const myFilteredPages = buildSignerPages(
        allPlaceholders,
        signerIdCandidates,
        signerEmailCandidates
      );
      const otherCompletedPages = buildOtherCompletedPages(
        allPlaceholders,
        signerIdCandidates,
        signerEmailCandidates
      );
      
      console.log("myFilteredPages (pages with my widgets):", myFilteredPages);
      console.log("myFilteredPages length:", myFilteredPages.length);
      
      // Check if biometric verification is required
      // First check signer-level override from NewUIRecipients (set in RequestSignPage)
      // This takes precedence over widget-level IdentityVerification from templates
      const newUIRecipients = documentData.NewUIRecipients || [];
      let signerRecipient = null;
      if (newUIRecipients.length > 0) {
        signerRecipient = newUIRecipients.find((r) => {
          const rId = normalizeValue(r?.objectId || r?.id);
          const rEmail = normalizeEmail(r?.email);
          return (
            (rId && signerIdCandidates.includes(rId)) ||
            (rEmail && signerEmailCandidates.includes(rEmail))
          );
        });
      }

      // If the signer has an explicit requireBiometric: false override, skip verification entirely
      // Only apply the override when requireBiometric and verificationSettings.enabled are consistent
      const signerHasExplicitOverride = signerRecipient && typeof signerRecipient.requireBiometric === "boolean";
      const signerVerificationEnabled = signerRecipient?.verificationSettings?.enabled === true;
      const signerRequiresBiometric = signerHasExplicitOverride ? signerRecipient.requireBiometric : null;
      // If requireBiometric: false but verificationSettings.enabled: true, the data is inconsistent
      // (template was saved with biometric on widgets but requireBiometric toggled off)
      // In that case, trust the widgets, not the override
      const isConsistentOverride = signerHasExplicitOverride && !signerRequiresBiometric && !signerVerificationEnabled;

      console.log("=== SIGNER-LEVEL BIOMETRIC CHECK ===");
      console.log("signerRecipient:", signerRecipient);
      console.log("signerHasExplicitOverride:", signerHasExplicitOverride);
      console.log("signerRequiresBiometric:", signerRequiresBiometric);
      console.log("signerVerificationEnabled:", signerVerificationEnabled);
      console.log("isConsistentOverride:", isConsistentOverride);

      // IdentityVerification is stored in each widget
      let isVerificationRequired = false;
      let verificationThreshold = 85;
      let verificationRequireDocumentBack = true;
      
      console.log("=== CHECKING IDENTITY VERIFICATION ===");

      // If signer-level override explicitly disables biometric, skip widget check
      if (isConsistentOverride) {
        console.log("✗ Biometric disabled by signer-level override (requireBiometric: false)");
        isVerificationRequired = false;
      } else {
        // Check if any widget for this signer has IdentityVerification enabled
        for (const page of myFilteredPages) {
          for (const widget of page.pos) {
            const idVerification = widget.IdentityVerification;
            console.log("Checking widget:", widget.key, "IdentityVerification:", idVerification);
            
            if (idVerification?.enabled === true) {
              isVerificationRequired = true;
              verificationThreshold = idVerification.threshold || 85;
              verificationRequireDocumentBack = resolveRequireDocumentBack(
                idVerification.requireDocumentBack,
                true
              );
              console.log("✓ Verification IS required for this signer");
              console.log("- threshold:", verificationThreshold);
              console.log("- requireDocumentBack:", verificationRequireDocumentBack);
              break;
            }
          }
          if (isVerificationRequired) break;
        }

        // Fallback: if signer could not be identified (no myFilteredPages),
        // check ALL placeholders in the document for IdentityVerification.
        // This ensures biometric is still required even when the signer context is missing.
        if (!isVerificationRequired && !myFilteredPages.length && allPlaceholders.length) {
          console.log("No signer-specific pages found, checking ALL placeholders for biometric requirement");
          for (const entry of allPlaceholders) {
            const widgets = Array.isArray(entry?.pos) ? entry.pos : [];
            for (const widget of widgets) {
              const idVerification = widget.IdentityVerification;
              if (idVerification?.enabled === true) {
                isVerificationRequired = true;
                verificationThreshold = idVerification.threshold || 85;
                verificationRequireDocumentBack = resolveRequireDocumentBack(
                  idVerification.requireDocumentBack,
                  true
                );
                console.log("✓ Verification required (document-level fallback)");
                break;
              }
            }
            if (isVerificationRequired) break;
          }
        }
      }
      
      if (!isVerificationRequired) {
        console.log("✗ No IdentityVerification found for this signer");
      }
      
      console.log("=== BIOMETRIC VERIFICATION FINAL STATE ===");
      console.log("isVerificationRequired:", isVerificationRequired);
      console.log("verificationThreshold:", verificationThreshold);
      console.log("verificationRequireDocumentBack:", verificationRequireDocumentBack);
      
      if (isVerificationRequired) {
        console.log(">>> SETTING VERIFICATION AS REQUIRED <<<");
        setVerificationRequired(true);
        setVerificationConfig({
          threshold: verificationThreshold,
          requireDocument: true,
          requireDocumentBack: verificationRequireDocumentBack
        });
        // Always require a fresh biometric capture for the current signing session.
        setVerificationComplete(false);
        setVerificationEvidence(null);
        console.log("Verification required for this signing session - requesting fresh capture");
      } else {
        console.log(">>> VERIFICATION NOT REQUIRED <<<");
        setVerificationComplete(false);
        setVerificationEvidence(null);
      }
      
      setMyPlaceholders(myFilteredPages);
      setOtherCompletedPlaceholders(otherCompletedPages);
      console.log("=== SET MY PLACEHOLDERS CALLED ===");
      console.log("Setting myPlaceholders to:", myFilteredPages);
      
      // Also get signer info from the first widget if signer not found
      if (!currentSigner && myFilteredPages.length > 0 && myFilteredPages[0].pos.length > 0) {
        const firstWidget = myFilteredPages[0].pos[0];
        const recipientName = firstWidget.options?.recipientName || "";
        const recipientEmail = firstWidget.options?.recipientEmail || "";
        
        console.log("Getting signer info from widget:", { recipientName, recipientEmail });
        
        if (recipientName || recipientEmail) {
          setSignerName(recipientName || currentParseUser?.get?.("name") || currentParseUser?.get?.("username") || "");
          setSignerEmail(recipientEmail || loggedUserEmail);
          const resolvedSignerId = effectiveContactBookId || loggedUserId || "";
          const resolvedSignerEmail = recipientEmail || loggedUserEmail || "";
          setSignerObjectId(resolvedSignerId);
          setSignerUserObjectId(loggedUserId || "");
          persistSignerContext(docId, {
            contactBookId: resolvedSignerId,
            email: resolvedSignerEmail
          });
        }
      }

      setPlaceholders(allPlaceholders);
    } catch (loadError) {
      console.error("Failed to load document", loadError);
      setError(loadError?.message || t("failed-to-load", { defaultValue: "Failed to load document" }));
    } finally {
      setIsLoading(false);
    }
  };

  // Initialize widget state from placeholders
  useEffect(() => {
    console.log("=== MY PLACEHOLDERS CHANGED ===");
    console.log("myPlaceholders:", myPlaceholders);
    console.log("myPlaceholders length:", myPlaceholders.length);
    
    if (myPlaceholders.length > 0) {
      console.log("Initializing widget state from placeholders");
      // Pass recipient info for prefilling
      initializeFromPlaceholders(myPlaceholders, {
        name: signerName,
        email: signerEmail
      }, {
        disableInitialsPrefill: true
      });
    } else {
      console.log("No placeholders to initialize");
    }
  }, [myPlaceholders, signerName, signerEmail, initializeFromPlaceholders]);

  // Show verification modal automatically if required and not complete
  useEffect(() => {
    if (
      !isLoading &&
      isAgreementAccepted &&
      verificationRequired &&
      !verificationComplete &&
      !isCompleted &&
      !isExpired &&
      !isDeclined &&
      !alreadySigned
    ) {
      console.log("=== AUTO-SHOWING VERIFICATION MODAL ===");
      setShowVerificationModal(true);
    }
  }, [
    isLoading,
    isAgreementAccepted,
    verificationRequired,
    verificationComplete,
    isCompleted,
    isExpired,
    isDeclined,
    alreadySigned
  ]);

  const getWidgetFromId = (widgetId) => {
    const parsed = parseWidgetId(widgetId);
    if (!parsed) {
      return null;
    }
    const page = myPlaceholders.find(
      (pageItem) => Number(pageItem?.pageNumber) === parsed.pageNumber
    );
    if (!page || !Array.isArray(page.pos)) {
      return null;
    }
    return page.pos[parsed.widgetIndex] || null;
  };

  const ensureSignatureImageMeetsThresholds = async (sourceUrl) => {
    const source = String(sourceUrl || "").trim();
    if (!source) {
      return false;
    }
    let measurableSource = source;
    if (!source.startsWith("data:image") && /^https?:\/\//i.test(source)) {
      measurableSource = await resolveSecureUrl(source);
    }
    const naturalWH = await getImageSize(measurableSource);
    return validateImageSignature(
      naturalWH,
      signatureValidation.minArea,
      signatureValidation.minLength
    );
  };

  // Handle widget click
  const handleWidgetClick = (widgetId, widgetType) => {
    const widgetData = getWidgetFromId(widgetId);
    if (!widgetData) {
      return;
    }
    if (!canCurrentSignerEditWidget(widgetData, widgetData?.__holderMeta || {})) {
      return;
    }
    setActiveWidgetId(widgetId);
    setActiveWidgetType(widgetType || 'signature');
    
    const currentWidget = getWidget(widgetId);
    
    // Open appropriate modal based on widget type
    if (widgetType === 'signature' || widgetType === 'initials') {
      setApplyInitialsToAllWidgets(false);
      setIsSignatureModalOpen(true);
    } else if (widgetType === 'checkbox') {
      setCheckboxLabel(resolveCheckboxLabel(widgetData, t));
      setCheckboxValue(parseCheckboxValue(currentWidget?.value));
      setIsCheckboxModalOpen(true);
    } else if (widgetType === 'text' || widgetType === 'name' || widgetType === 'email' || widgetType === 'job title' || widgetType === 'company') {
      setTextInputValue(currentWidget?.value || '');
      setIsTextModalOpen(true);
    } else if (widgetType === 'date') {
      setDateValue(currentWidget?.value || new Date().toISOString().slice(0, 10));
      setIsDateModalOpen(true);
    } else if (widgetType === 'image' || widgetType === 'stamp') {
      setImagePreview(currentWidget?.value || '');
      setIsImageModalOpen(true);
    }
  };

  const getEditableInitialsWidgets = () => {
    const entries = [];
    myPlaceholders.forEach((page) => {
      (page?.pos || []).forEach((widget, widgetIndex) => {
        const widgetType = String(widget?.type || "").trim().toLowerCase();
        if (widgetType !== "initials") {
          return;
        }
        if (!canCurrentSignerEditWidget(widget, widget?.__holderMeta || {})) {
          return;
        }
        entries.push({
          widgetId: `${page.pageNumber}-${widgetIndex}`,
          widget,
          pageNumber: Number(page.pageNumber) || 1,
          widgetIndex
        });
      });
    });
    return entries;
  };

  // Handle signature save
  const handleSignatureSave = async (signatureDataUrl, options = {}) => {
    if (!activeWidgetId) return;

    try {
      if (activeWidgetType === "signature") {
        // Validate drawn signature stroke metrics if available
        const { drawnArea, drawnLength } = options;
        if (typeof drawnArea === "number" && typeof drawnLength === "number") {
          if (drawnArea < signatureValidation.minArea || drawnLength < signatureValidation.minLength) {
            setAlert({
              show: true,
              type: "warning",
              message: t("signature-too-small", {
                defaultValue: "The signature is too small. Please draw a larger signature."
              })
            });
            return;
          }
        } else {
          // Fallback: validate image dimensions for uploaded/typed signatures
          const valid = await ensureSignatureImageMeetsThresholds(signatureDataUrl);
          if (!valid) {
            setAlert({
              show: true,
              type: "warning",
              message: t("signature-too-small", {
                defaultValue: "The signature is too small. Please draw a larger signature."
              })
            });
            return;
          }
        }
      }

      const shouldApplyToAllInitials =
        activeWidgetType === "initials" && options?.applyToAllInitials === true;

      if (shouldApplyToAllInitials) {
        const initialsWidgets = getEditableInitialsWidgets();
        initialsWidgets.forEach((entry) => {
          updateWidget(entry.widgetId, signatureDataUrl, {
            type: "initials",
            required: isWidgetRequired(entry.widget),
            pageNumber: entry.pageNumber,
            widgetIndex: entry.widgetIndex
          });
        });
      } else {
        const [pageNum, widgetIdx] = activeWidgetId.split('-').map(Number);
        const widgetData = getWidgetFromId(activeWidgetId);
        if (!canCurrentSignerEditWidget(widgetData, widgetData?.__holderMeta || {})) {
          return;
        }
        
        updateWidget(activeWidgetId, signatureDataUrl, {
          type: activeWidgetType,
          required: isWidgetRequired(widgetData),
          pageNumber: pageNum,
          widgetIndex: widgetIdx
        });
      }

      setIsSignatureModalOpen(false);
      setApplyInitialsToAllWidgets(false);
      setActiveWidgetId(null);
    } catch (error) {
      console.error('Error saving signature:', error);
    }
  };

  // Handle text input save
  const handleTextSave = () => {
    if (!activeWidgetId) return;
    
    const [pageNum, widgetIdx] = activeWidgetId.split('-').map(Number);
    const widgetData = getWidgetFromId(activeWidgetId);
    if (!canCurrentSignerEditWidget(widgetData, widgetData?.__holderMeta || {})) {
      return;
    }
    
    updateWidget(activeWidgetId, textInputValue, {
      type: activeWidgetType,
      required: isWidgetRequired(widgetData),
      pageNumber: pageNum,
      widgetIndex: widgetIdx
    });

    setIsTextModalOpen(false);
    setTextInputValue('');
    setActiveWidgetId(null);
  };

  // Handle checkbox save
  const handleCheckboxSave = () => {
    if (!activeWidgetId) return;
    
    const [pageNum, widgetIdx] = activeWidgetId.split('-').map(Number);
    const widgetData = getWidgetFromId(activeWidgetId);
    if (!canCurrentSignerEditWidget(widgetData, widgetData?.__holderMeta || {})) {
      return;
    }
    
    updateWidget(activeWidgetId, checkboxValue, {
      type: 'checkbox',
      required: isWidgetRequired(widgetData),
      pageNumber: pageNum,
      widgetIndex: widgetIdx
    });

    setIsCheckboxModalOpen(false);
    setCheckboxValue(false);
    setActiveWidgetId(null);
  };

  // Handle date save
  const handleDateSave = () => {
    if (!activeWidgetId) return;
    
    const [pageNum, widgetIdx] = activeWidgetId.split('-').map(Number);
    const widgetData = getWidgetFromId(activeWidgetId);
    if (!canCurrentSignerEditWidget(widgetData, widgetData?.__holderMeta || {})) {
      return;
    }
    
    updateWidget(activeWidgetId, dateValue, {
      type: 'date',
      required: isWidgetRequired(widgetData),
      pageNumber: pageNum,
      widgetIndex: widgetIdx
    });

    setIsDateModalOpen(false);
    setDateValue('');
    setActiveWidgetId(null);
  };

  // Handle image upload
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setAlert({
        show: true,
        type: 'error',
        message: t('invalid-image-type', { defaultValue: 'Por favor seleccione un archivo de imagen válido' })
      });
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setAlert({
        show: true,
        type: 'error',
        message: t('image-too-large', { defaultValue: 'La imagen es demasiado grande. Máximo 5MB' })
      });
      return;
    }

    // Convert to base64
    setIsImageLoading(true);
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
      setImageFile(file);
      setIsImageLoading(false);
    };
    reader.onerror = () => {
      setIsImageLoading(false);
      setAlert({
        show: true,
        type: 'error',
        message: t('image-read-error', { defaultValue: 'Error al leer la imagen' })
      });
    };
    reader.readAsDataURL(file);
  };

  // Handle image save
  const handleImageSave = () => {
    if (!activeWidgetId || !imagePreview) return;
    
    const [pageNum, widgetIdx] = activeWidgetId.split('-').map(Number);
    const widgetData = getWidgetFromId(activeWidgetId);
    if (!canCurrentSignerEditWidget(widgetData, widgetData?.__holderMeta || {})) {
      return;
    }
    
    updateWidget(activeWidgetId, imagePreview, {
      type: activeWidgetType,
      required: isWidgetRequired(widgetData),
      pageNumber: pageNum,
      widgetIndex: widgetIdx
    });

    setIsImageModalOpen(false);
    setImagePreview('');
    setImageFile(null);
    setActiveWidgetId(null);
  };

  const handleZoomIn = () => {
    setZoomScale((current) => Math.min(MAX_ZOOM, current + ZOOM_STEP));
  };

  const handleZoomOut = () => {
    setZoomScale((current) => Math.max(MIN_ZOOM, current - ZOOM_STEP));
  };

  const handlePreviousPage = () => {
    setCurrentPageNumber((current) => Math.max(1, current - 1));
  };

  const handleNextPage = () => {
    setCurrentPageNumber((current) => Math.min(numPages, current + 1));
  };

  const handleFirstPage = () => {
    setCurrentPageNumber(1);
  };

  const handleLastPage = () => {
    setCurrentPageNumber(numPages);
  };

  const handlePageLoadSuccess = (pageProxy) => {
    const originalWidth = Number(
      pageProxy?.originalWidth ||
        (Array.isArray(pageProxy?.view) ? pageProxy.view[2] - pageProxy.view[0] : 0)
    );
    const originalHeight = Number(
      pageProxy?.originalHeight ||
        (Array.isArray(pageProxy?.view) ? pageProxy.view[3] - pageProxy.view[1] : 0)
    );
    if (!Number.isFinite(originalWidth) || originalWidth <= 0) {
      return;
    }
    setPageOriginalWidths((prev) => {
      if (prev?.[currentPageNumber] === originalWidth) {
        return prev;
      }
      return { ...prev, [currentPageNumber]: originalWidth };
    });
    if (Number.isFinite(originalHeight) && originalHeight > 0) {
      setPageOriginalHeights((prev) => {
        if (prev?.[currentPageNumber] === originalHeight) {
          return prev;
        }
        return { ...prev, [currentPageNumber]: originalHeight };
      });
    }
  };

  const countPendingRequiredInitials = () => {
    let missing = 0;
    myPlaceholders.forEach((page) => {
      (page?.pos || []).forEach((widget, widgetIndex) => {
        const widgetType = String(widget?.type || "").trim().toLowerCase();
        if (widgetType !== "initials" || !isWidgetRequired(widget)) {
          return;
        }
        if (!canCurrentSignerEditWidget(widget, widget?.__holderMeta || {})) {
          return;
        }
        const widgetId = `${page.pageNumber}-${widgetIndex}`;
        const widgetStateData = getWidget(widgetId);
        const isCompleted = Boolean(widgetStateData?.completed);
        const isAutoSuggested = widgetStateData?.autoSuggested === true;
        if (!isCompleted || isAutoSuggested) {
          missing += 1;
        }
      });
    });
    return missing;
  };

  const handleCompleteDocument = async () => {
    if (alreadySigned) {
      setAlert({
        show: true,
        type: "info",
        message: t("already-signed-message", {
          defaultValue: "Ya has firmado este documento anteriormente."
        })
      });
      return;
    }

    if (!isAgreementAccepted) {
      setAlert({
        show: true,
        type: "warning",
        message: t("agrrement-alert", {
          defaultValue:
            "Debes aceptar la divulgación de firma electrónica para continuar con el proceso."
        })
      });
      return;
    }

    // Check if biometric verification is required but not complete
    if (verificationRequired && !verificationComplete) {
      setShowVerificationModal(true);
      return;
    }

    // Check if all required widgets are filled
    if (!isAllRequiredFilled()) {
      setAlert({
        show: true,
        type: 'warning',
        message: t("fill-required-fields", { defaultValue: "Por favor complete todos los campos requeridos" })
      });
      return;
    }

    const pendingInitials = countPendingRequiredInitials();
    if (pendingInitials > 0) {
      setAlert({
        show: true,
        type: "warning",
        message: t("verification.required-initials-missing", {
          defaultValue:
            "Faltan iniciales requeridas por completar. Debes colocar tus iniciales en todos los campos solicitados antes de enviar."
        })
      });
      return;
    }

    // Show confirmation dialog
    setShowConfirmDialog(true);
  };

  const handleDeclineDocument = () => {
    if (alreadySigned) {
      setAlert({
        show: true,
        type: "info",
        message: t("already-signed-message", {
          defaultValue: "Ya has firmado este documento anteriormente."
        })
      });
      return;
    }
    setDeclineReason("");
    setShowDeclineConfirmDialog(true);
  };

  const handleConfirmDecline = async () => {
    setShowDeclineConfirmDialog(false);
    setIsSubmitting(true);

    try {
      const currentParseUser = Parse.User.current();
      const currentUserId = normalizeValue(currentParseUser?.id || currentParseUser?.objectId);
      const declineByUserId =
        currentUserId ||
        normalizeValue(signerUserObjectId) ||
        normalizeValue(signerObjectId) ||
        "";

      const declinePayload = {
        docId
      };
      if (declineByUserId) {
        declinePayload.userId = declineByUserId;
      }
      const normalizedReason = String(declineReason || "").trim();
      if (normalizedReason) {
        declinePayload.reason = normalizedReason;
      }

      await Parse.Cloud.run("declinedoc", declinePayload);
      setIsDeclined(true);
      setDeclineReason("");
      setAlert({
        show: true,
        type: "success",
        message: t("verification.document-declined", {
          defaultValue: "Este documento ha sido rechazado"
        })
      });

      if (redirectUrl && redirectUrl.trim()) {
        setTimeout(() => {
          window.location.href = redirectUrl;
        }, 1200);
      }
    } catch (declineError) {
      console.error("Failed to decline document", declineError);
      setAlert({
        show: true,
        type: "error",
        message:
          t("submission-error", {
            defaultValue: "Error al enviar el documento. Por favor intente nuevamente."
          }) + (declineError?.message ? `\n${declineError.message}` : "")
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerificationSuccess = (evidence) => {
    console.log("=== VERIFICATION SUCCESS ===");
    console.log("Evidence:", evidence);
    
    setVerificationEvidence(evidence);
    setVerificationComplete(true);
    setShowVerificationModal(false);
    
    setAlert({
      show: true,
      type: 'success',
      message: t("verification.success-complete", { 
        defaultValue: "Verificación de identidad completada exitosamente." 
      })
    });
  };

  const handleVerificationClose = () => {
    setShowVerificationModal(false);
  };

  const handleConfirmSubmit = async () => {
    setShowConfirmDialog(false);
    setIsSubmitting(true);

    try {
      const fallbackSignerId =
        normalizeValue(effectiveContactBookId) ||
        normalizeValue(signerObjectId) ||
        normalizeEmail(signerEmail);

      if (!fallbackSignerId) {
        throw new Error(
          t("verification.signer-context-missing", {
            defaultValue: "No se pudo identificar al firmante actual."
          })
        );
      }

      console.log("=== SUBMITTING EXTERNAL SIGNING ===");
      console.log("Document ID:", docId);
      console.log("Contact Book ID:", effectiveContactBookId);
      console.log("Effective signer ID:", fallbackSignerId);
      console.log("Widget State:", widgetState);

      const pendingInitials = countPendingRequiredInitials();
      if (pendingInitials > 0) {
        throw new Error(
          t("verification.required-initials-missing", {
            defaultValue:
              "Faltan iniciales requeridas por completar. Debes colocar tus iniciales en todos los campos solicitados antes de enviar."
          })
        );
      }
      
      // Build updated placeholders with widget responses
      const updatedPlaceholders = myPlaceholders.map((page) => {
        const updatedPos = page.pos.map((widget, widgetIndex) => {
          const widgetId = `${page.pageNumber}-${widgetIndex}`;
          const widgetData = getWidget(widgetId);
          const isEditableWidget = canCurrentSignerEditWidget(widget, widget?.__holderMeta || {});
          
          if (isEditableWidget && widgetData && widgetData.completed) {
            const imageWidgetTypes = ['signature', 'initials', 'stamp', 'image'];
            const isImageWidget = imageWidgetTypes.includes(widget?.type);
            return {
              ...widget,
              ...(isImageWidget && typeof widgetData.value === "string"
                ? { SignUrl: widgetData.value }
                : {}),
              options: {
                ...widget.options,
                ...(isImageWidget && typeof widgetData.value === "string"
                  ? { SignUrl: widgetData.value }
                  : {}),
                response: widgetData.value
              }
            };
          }
          return widget;
        });
        
        return {
          ...page,
          pos: updatedPos
        };
      });
      
      console.log("Updated Placeholders:", JSON.stringify(updatedPlaceholders, null, 2));

      const signatureWidgets = [];
      updatedPlaceholders.forEach((page) => {
        (page?.pos || []).forEach((widget) => {
          if (String(widget?.type || "").trim().toLowerCase() !== "signature") {
            return;
          }
          const response = String(
            widget?.options?.response || widget?.SignUrl || widget?.options?.SignUrl || ""
          ).trim();
          if (response) {
            signatureWidgets.push(response);
          }
        });
      });
      for (let i = 0; i < signatureWidgets.length; i += 1) {
        const valid = await ensureSignatureImageMeetsThresholds(signatureWidgets[i]);
        if (!valid) {
          throw new Error(
            t("signature-too-small", {
              defaultValue: "The signature is too small. Please draw a larger signature."
            })
          );
        }
      }

      // Call backend function to complete external signing
      const result = await Parse.Cloud.run('completeExternalSigning', {
        docId: docId,
        contactBookId: fallbackSignerId,
        widgetResponses: updatedPlaceholders,
        verificationEvidence: verificationEvidence,
        signerContext: {
          objectId: normalizeValue(signerObjectId),
          email: signerEmail,
          name: signerName
        }
      });

      console.log("=== COMPLETION RESULT ===");
      console.log(result);

      const normalizedSigningResult = {
        isCompleted: Boolean(result?.isCompleted),
        signedCount: Number(result?.signedCount || 0),
        totalSigners: Number(result?.totalSigners || 0)
      };
      setSigningResult(normalizedSigningResult);
      setIsCompleted(normalizedSigningResult.isCompleted);

      if (result?.status === "already_signed") {
        setAlert({
          show: true,
          type: "info",
          message:
            result?.message ||
            t("already-signed-message", {
              defaultValue: "Ya has firmado este documento anteriormente."
            })
        });
        await loadDocument();
        setIsSubmitting(false);
        return;
      }

      if (result?.status === "waiting_turn") {
        setAlert({
          show: true,
          type: "info",
          message:
            result?.message ||
            t("new-ui.dashboard.pending-documents.waiting-turn", {
              defaultValue: "Waiting for previous signer."
            })
        });
        setIsSubmitting(false);
        return;
      }

      if (result?.isCompleted) {
        const latestDocResponse = await Parse.Cloud.run("getDocument", { docId }).catch(() => null);
        const latestDoc = normalizeDocumentPayload(latestDocResponse);
        const { signedUrl, certificateUrl } = await resolveDownloadAssets(latestDoc, {
          ensureCertificate: true
        });
        if (signedUrl) {
          setSignedDownloadUrl(signedUrl);
          setPdfUrl(signedUrl);
          setPdfFile(signedUrl);
        }
        if (certificateUrl) {
          setCertificateDownloadUrl(certificateUrl);
        }
      }

      // Clear widget state from localStorage
      clearState();

      // Show success message
      setAlert({
        show: true,
        type: 'success',
        message: normalizedSigningResult.isCompleted
          ? t("verification.document-completed", {
              defaultValue: "Documento completado exitosamente. Se han enviado notificaciones a todas las partes."
            })
          : t("verification.signature-recorded", {
              defaultValue: "Tu firma ha sido registrada exitosamente."
            })
      });

      // Wait a moment to show the success message
      setTimeout(() => {
        // Check if there's a redirect URL configured
        if (redirectUrl && redirectUrl.trim()) {
          // Redirect to the configured URL
          window.location.href = redirectUrl;
        } else {
          // Show success screen
          setShowSuccessScreen(true);
        }
      }, 2000);

    } catch (submitError) {
      console.error('Failed to submit document', submitError);
      setAlert({
        show: true,
        type: 'error',
        message: t("submission-error", { 
          defaultValue: "Error al enviar el documento. Por favor intente nuevamente." 
        }) + (submitError?.message ? `\n${submitError.message}` : '')
      });
      setIsSubmitting(false);
    }
  };

  // Count total fields to sign
  const totalFieldsCount = myPlaceholders.reduce((count, page) => {
    const signableWidgets = (page.pos || []).filter((widget) => !isPrefillWidget(widget, page));
    return count + signableWidgets.length;
  }, 0);
  const remainingSigners = Math.max(
    0,
    Number(signingResult.totalSigners || 0) - Number(signingResult.signedCount || 0)
  );
  const currentPath = String(location?.pathname || "");
  const isRecipientTourRoute =
    currentPath.includes("/recipientSignPdf/") || currentPath.includes("/pdfRequestFiles/");
  const recipientTourSteps = [
    {
      selector: '[data-tut="tour-signer-info"]',
      message: t("tour-mssg.pdf-request-file-1", {
        defaultValue: "Revisa la información del firmante antes de completar el documento."
      })
    },
    {
      selector: '[data-tut="tour-fields-progress"]',
      message: t("tour-mssg.pdf-request-file-6", {
        defaultValue: "Aquí verás el progreso de los campos requeridos que te faltan por completar."
      })
    },
    {
      selector: '[data-tut="tour-pdf-area"]',
      message: t("tour-mssg.pdf-request-file-2", {
        defaultValue: "Haz clic sobre los widgets dentro del PDF para completar la firma y los campos."
      })
    },
    {
      selector: '[data-tut="tour-navigation-controls"]',
      message: t("tour-mssg.pdf-request-file-3", {
        defaultValue: "Usa estos controles para navegar entre páginas del documento."
      })
    },
    {
      selector: '[data-tut="tour-submit-actions"]',
      message: t("tour-mssg.pdf-request-file-4", {
        defaultValue: "Cuando termines, puedes declinar o completar el documento desde aquí."
      })
    }
  ];
  const safeRecipientTourStep = Math.min(
    Math.max(recipientTourStep, 0),
    Math.max(recipientTourSteps.length - 1, 0)
  );
  const activeRecipientTourStep = recipientTourSteps[safeRecipientTourStep] || null;

  const goToPreviousRecipientTourStep = () => {
    // Tour removed
  };

  useEffect(() => {
    if (!isRecipientTourRoute || !isTourEnabled) {
      return;
    }
    if (
      isLoading ||
      Boolean(error) ||
      !isAgreementAccepted ||
      isCompleted ||
      isExpired ||
      isDeclined ||
      alreadySigned ||
      showSuccessScreen ||
      !pdfFile
    ) {
      return;
    }
    if (verificationRequired && !verificationComplete) {
      return;
    }
    if (localStorage.getItem(RECIPIENT_TOUR_HIDE_KEY) === "1") {
      return;
    }
    if (localStorage.getItem(RECIPIENT_TOUR_SEEN_KEY) === "1") {
      return;
    }
    setShowRecipientTour(true);
    setRecipientTourStep(0);
  }, [
    isRecipientTourRoute,
    isTourEnabled,
    isLoading,
    error,
    isCompleted,
    isExpired,
    isDeclined,
    alreadySigned,
    showSuccessScreen,
    isAgreementAccepted,
    verificationRequired,
    verificationComplete,
    pdfFile
  ]);

  useEffect(() => {
    if (!showRecipientTour || !activeRecipientTourStep?.selector || typeof document === "undefined") {
      return;
    }
    const updateTourTargetRect = () => {
      const targetElement = document.querySelector(activeRecipientTourStep.selector);
      if (!targetElement) {
        setRecipientTourTargetRect(null);
        return;
      }
      const rect = targetElement.getBoundingClientRect();
      setRecipientTourTargetRect({
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
        bottom: rect.bottom
      });
    };
    updateTourTargetRect();
    window.addEventListener("resize", updateTourTargetRect);
    window.addEventListener("scroll", updateTourTargetRect, true);
    return () => {
      window.removeEventListener("resize", updateTourTargetRect);
      window.removeEventListener("scroll", updateTourTargetRect, true);
    };
  }, [showRecipientTour, activeRecipientTourStep, currentPageNumber, zoomScale, numPages]);

  const recipientTourTooltipStyle = (() => {
    if (typeof window === "undefined") {
      return { top: 24, left: 24 };
    }
    if (!recipientTourTargetRect) {
      return {
        top: Math.max(24, window.innerHeight / 2 - 120),
        left: Math.max(24, window.innerWidth / 2 - RECIPIENT_TOUR_TOOLTIP_WIDTH / 2)
      };
    }
    const maxLeft = Math.max(16, window.innerWidth - RECIPIENT_TOUR_TOOLTIP_WIDTH - 16);
    const left = Math.min(Math.max(16, recipientTourTargetRect.left), maxLeft);
    const estimatedHeight = 210;
    let top = recipientTourTargetRect.bottom + 12;
    if (top + estimatedHeight > window.innerHeight - 16) {
      top = Math.max(16, recipientTourTargetRect.top - estimatedHeight - 12);
    }
    return { top, left };
  })();

  // Status message
  const getStatusMessage = () => {
    if (isCompleted) {
      return t("verification.document-completed", {
        defaultValue: "Documento completado exitosamente. Se han enviado notificaciones a todas las partes."
      });
    }
    if (isDeclined) {
      return t("verification.document-declined", {
        defaultValue: "Este documento ha sido rechazado"
      });
    }
    if (isExpired) {
      return t("verification.document-expired", {
        defaultValue: "Este documento ha expirado"
      });
    }
    // Don't return message for alreadySigned - we'll handle it separately with a custom screen
    return null;
  };

  const statusMessage = getStatusMessage();

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-app-sand">
        <Card className="w-full max-w-md">
          <div className="flex flex-col items-center gap-4 py-8">
            <div className="h-12 w-12 animate-spin rounded-full border-4 border-app-brand border-t-transparent"></div>
            <p className="text-sm text-app-ink-muted">
              {t("loading-document", { defaultValue: "Loading document..." })}
            </p>
          </div>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-app-sand px-4">
        <Card className="w-full max-w-md">
          <div className="flex flex-col items-center gap-4 py-8">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
              <Icon name="close" className="h-6 w-6 text-red-600" />
            </div>
            <h2 className="text-lg font-semibold text-app-ink">
              {t("error-loading-document", { defaultValue: "Error loading document" })}
            </h2>
            <p className="text-center text-sm text-app-ink-muted">{error}</p>
            <Button onClick={() => window.location.reload()}>
              {t("try-again", { defaultValue: "Try again" })}
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  if (statusMessage) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-app-sand px-4">
        <Card className="w-full max-w-lg">
          <div className="flex flex-col items-center gap-6 py-12 px-6">
            {/* Success Icon with animation */}
            <div className="relative animate-fade-up">
              <div className="flex h-20 w-20 items-center justify-center rounded-full bg-green-100">
                <svg 
                  className="h-10 w-10 text-green-600" 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M5 13l4 4L19 7" 
                  />
                </svg>
              </div>
            </div>

            {/* Message */}
            <div className="text-center space-y-2">
              <h2 className="text-2xl font-bold text-app-ink">
                {isCompleted &&
                  t("verification.document-completed-title", { defaultValue: "Documento completado" })}
                {isDeclined &&
                  t("verification.document-declined-title", { defaultValue: "Documento rechazado" })}
                {isExpired &&
                  t("verification.document-expired-title", { defaultValue: "Documento expirado" })}
              </h2>
              <p className="text-base text-app-ink-muted">
                {statusMessage}
              </p>
              {docId && isCompleted && (
                <p className="text-xs text-app-ink-muted font-mono mt-1">
                  Envelope ID: {docId}
                </p>
              )}
            </div>

            {/* Document Info */}
            <div className="w-full rounded-lg bg-app-sand p-4 space-y-2">
              <div className="flex items-center gap-2">
                <svg 
                  className="h-5 w-5 text-app-ink-muted flex-shrink-0" 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" 
                  />
                </svg>
                <span className="text-sm font-medium text-app-ink">{documentName}</span>
              </div>
              {signerName && (
                <div className="flex items-center gap-2">
                  <svg 
                    className="h-5 w-5 text-app-ink-muted flex-shrink-0" 
                    fill="none" 
                    viewBox="0 0 24 24" 
                    stroke="currentColor"
                  >
                    <path 
                      strokeLinecap="round" 
                      strokeLinejoin="round" 
                      strokeWidth={2} 
                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" 
                    />
                  </svg>
                  <span className="text-sm text-app-ink-muted">{signerName}</span>
                </div>
              )}
              {signerEmail && (
                <div className="flex items-center gap-2">
                  <svg 
                    className="h-5 w-5 text-app-ink-muted flex-shrink-0" 
                    fill="none" 
                    viewBox="0 0 24 24" 
                    stroke="currentColor"
                  >
                    <path 
                      strokeLinecap="round" 
                      strokeLinejoin="round" 
                      strokeWidth={2} 
                      d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" 
                    />
                  </svg>
                  <span className="text-sm text-app-ink-muted">{signerEmail}</span>
                </div>
              )}
            </div>

            {isCompleted && (
              <div className="w-full space-y-2">
                <Button onClick={handleDownloadSigned} disabled={!signedDownloadUrl} className="w-full">
                  {t("new-ui.individual-signing.download-signed", {
                    defaultValue: "Download signed document"
                  })}
                </Button>
                <Button
                  variant="secondary"
                  onClick={handleDownloadCertificate}
                  disabled={isPreparingCertificate}
                  className="w-full"
                >
                  {isPreparingCertificate
                    ? t("generating-certificate", { defaultValue: "Generating certificate" })
                    : certificateDownloadUrl
                      ? t("new-ui.individual-signing.download-certificate", {
                          defaultValue: "Download certificate"
                        })
                      : t("new-ui.individual-signing.generate-certificate", {
                          defaultValue: "Generate certificate"
                        })}
                </Button>
              </div>
            )}

            {/* Powered by DANAsign */}
            <div className="pt-4 border-t border-app-line w-full">
              <a 
                href="https://danasign.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 text-sm text-app-ink-muted hover:text-app-brand transition-colors"
              >
                <span>{t("powered-by", { defaultValue: "Powered by" })}</span>
                <span className="font-semibold">DANAsign</span>
              </a>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  // Already signed screen - show confirmation with signed date
  if (alreadySigned) {
    const formatSignedDate = (date) => {
      if (!date) return '';
      try {
        return new Intl.DateTimeFormat(i18n.language || 'es', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        }).format(date);
      } catch (error) {
        return date.toLocaleString();
      }
    };

    return (
      <div className="flex min-h-screen items-center justify-center bg-app-sand px-4">
        <Card className="w-full max-w-lg">
          <div className="flex flex-col items-center gap-6 py-12 px-6">
            {/* Success Icon */}
            <div className="relative">
              <div className="flex h-20 w-20 items-center justify-center rounded-full bg-green-100">
                <svg 
                  className="h-10 w-10 text-green-600" 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M5 13l4 4L19 7" 
                  />
                </svg>
              </div>
            </div>

            {/* Message */}
            <div className="text-center space-y-2">
              <h2 className="text-2xl font-bold text-app-ink">
                {t("already-signed-title", { defaultValue: "Documento ya firmado" })}
              </h2>
              <p className="text-base text-app-ink-muted">
                {t("already-signed-message", { 
                  defaultValue: "Ya has firmado este documento anteriormente." 
                })}
              </p>
            </div>

            {/* Document Info */}
            <div className="w-full rounded-lg bg-app-sand p-4 space-y-2">
              <div className="flex items-center gap-2">
                <svg 
                  className="h-5 w-5 text-app-ink-muted flex-shrink-0" 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" 
                  />
                </svg>
                <span className="text-sm font-medium text-app-ink">{documentName}</span>
              </div>
              {signerName && (
                <div className="flex items-center gap-2">
                  <svg 
                    className="h-5 w-5 text-app-ink-muted flex-shrink-0" 
                    fill="none" 
                    viewBox="0 0 24 24" 
                    stroke="currentColor"
                  >
                    <path 
                      strokeLinecap="round" 
                      strokeLinejoin="round" 
                      strokeWidth={2} 
                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" 
                    />
                  </svg>
                  <span className="text-sm text-app-ink-muted">{signerName}</span>
                </div>
              )}
              {signerEmail && (
                <div className="flex items-center gap-2">
                  <svg 
                    className="h-5 w-5 text-app-ink-muted flex-shrink-0" 
                    fill="none" 
                    viewBox="0 0 24 24" 
                    stroke="currentColor"
                  >
                    <path 
                      strokeLinecap="round" 
                      strokeLinejoin="round" 
                      strokeWidth={2} 
                      d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" 
                    />
                  </svg>
                  <span className="text-sm text-app-ink-muted">{signerEmail}</span>
                </div>
              )}
              {signedDate && (
                <div className="flex items-center gap-2 pt-2 border-t border-app-line">
                  <svg 
                    className="h-5 w-5 text-app-ink-muted flex-shrink-0" 
                    fill="none" 
                    viewBox="0 0 24 24" 
                    stroke="currentColor"
                  >
                    <path 
                      strokeLinecap="round" 
                      strokeLinejoin="round" 
                      strokeWidth={2} 
                      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" 
                    />
                  </svg>
                  <div className="flex flex-col">
                    <span className="text-xs text-app-ink-muted">
                      {t("signed-on", { defaultValue: "Firmado el" })}
                    </span>
                    <span className="text-sm font-medium text-app-ink">
                      {formatSignedDate(signedDate)}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Powered by DANAsign */}
            <div className="pt-4 border-t border-app-line w-full">
              <a 
                href="https://danasign.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 text-sm text-app-ink-muted hover:text-app-brand transition-colors"
              >
                <span>{t("powered-by", { defaultValue: "Powered by" })}</span>
                <span className="font-semibold">DANAsign</span>
              </a>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  if (!isAgreementAccepted && !isCompleted && !isDeclined && !isExpired && !alreadySigned) {
    return (
      <AgreementSign
        onAccept={() => setIsAgreementAccepted(true)}
        signerName={signerName}
        documentName={documentName}
      />
    );
  }

  // Verification required screen - blocks access to document until verified
  if (verificationRequired && !verificationComplete) {
    return (
      <>
        <div className="flex min-h-screen items-center justify-center bg-app-sand px-4">
          <Card className="w-full max-w-md">
            <div className="flex flex-col items-center gap-4 py-8">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-blue-100">
                <Icon name="user" className="h-8 w-8 text-blue-600" />
              </div>
              <h2 className="text-lg font-semibold text-app-ink text-center">
                {t("verification.required", { defaultValue: "Verificación requerida" })}
              </h2>
              <p className="text-center text-sm text-app-ink-muted">
                {t("verification.required-message", { 
                  defaultValue: "Este documento requiere verificación de identidad antes de continuar. Por favor complete el proceso de verificación biométrica." 
                })}
              </p>
              <Button onClick={() => setShowVerificationModal(true)} className="mt-4">
                <Icon name="user" className="h-4 w-4 mr-2" />
                {t("verification.start-verification", { defaultValue: "Iniciar verificación" })}
              </Button>
              
              {/* Document info */}
              <div className="w-full mt-4 pt-4 border-t border-app-line">
                <p className="text-xs text-app-ink-muted text-center mb-2">
                  {t("document", { defaultValue: "Documento" })}
                </p>
                <p className="text-sm font-medium text-app-ink text-center">{documentName}</p>
                {signerName && (
                  <p className="text-xs text-app-ink-muted text-center mt-2">
                    {t("for", { defaultValue: "Para" })}: {signerName}
                  </p>
                )}
              </div>
            </div>
          </Card>
        </div>

        {/* Verification Modal - must be rendered even in blocking screen */}
        <VerificationModal
          isOpen={showVerificationModal}
          onClose={handleVerificationClose}
          onSuccess={handleVerificationSuccess}
          signer={{
            name: signerName,
            email: signerEmail,
            objectId: signerObjectId
          }}
          documentId={docId}
          verificationConfig={verificationConfig}
        />
      </>
    );
  }

  // Success screen after completing document
  if (showSuccessScreen) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-app-sand px-4">
        <Card className="w-full max-w-lg">
          <div className="flex flex-col items-center gap-6 py-12 px-6">
            {/* Success Icon */}
            <div className="relative">
              <div className="flex h-20 w-20 items-center justify-center rounded-full bg-green-100 animate-pulse">
                <svg 
                  className="h-10 w-10 text-green-600" 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M5 13l4 4L19 7" 
                  />
                </svg>
              </div>
              {/* Checkmark animation ring */}
              <div className="absolute inset-0 rounded-full border-4 border-green-200 animate-ping"></div>
            </div>

            {/* Success Message */}
            <div className="text-center space-y-2">
              <h2 className="text-2xl font-bold text-app-ink">
                {signingResult.isCompleted
                  ? t("verification.document-completed-title", { defaultValue: "¡Documento completado!" })
                  : t("verification.signature-recorded-title", { defaultValue: "¡Firma registrada!" })}
              </h2>
              <p className="text-base text-app-ink-muted">
                {signingResult.isCompleted
                  ? t("verification.document-completed-message", {
                      defaultValue: "Has completado exitosamente el documento."
                    })
                  : t("verification.pending-other-signers-message", {
                      defaultValue:
                        "Tu firma fue registrada. Este documento se completará cuando los demás firmantes finalicen."
                    })}
              </p>
            </div>

            {/* Document Info */}
            <div className="w-full rounded-lg bg-app-sand p-4 space-y-2">
              <div className="flex items-center gap-2">
                <svg 
                  className="h-5 w-5 text-app-ink-muted flex-shrink-0" 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" 
                  />
                </svg>
                <span className="text-sm font-medium text-app-ink">{documentName}</span>
              </div>
              {signerName && (
                <div className="flex items-center gap-2">
                  <svg 
                    className="h-5 w-5 text-app-ink-muted flex-shrink-0" 
                    fill="none" 
                    viewBox="0 0 24 24" 
                    stroke="currentColor"
                  >
                    <path 
                      strokeLinecap="round" 
                      strokeLinejoin="round" 
                      strokeWidth={2} 
                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" 
                    />
                  </svg>
                  <span className="text-sm text-app-ink-muted">{signerName}</span>
                </div>
              )}
              {signerEmail && (
                <div className="flex items-center gap-2">
                  <svg 
                    className="h-5 w-5 text-app-ink-muted flex-shrink-0" 
                    fill="none" 
                    viewBox="0 0 24 24" 
                    stroke="currentColor"
                  >
                    <path 
                      strokeLinecap="round" 
                      strokeLinejoin="round" 
                      strokeWidth={2} 
                      d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" 
                    />
                  </svg>
                  <span className="text-sm text-app-ink-muted">{signerEmail}</span>
                </div>
              )}
            </div>

            {/* Success Details */}
            <div className="w-full text-center space-y-1">
              {docId && (
                <p className="text-xs text-app-ink-muted font-mono">
                  Envelope ID: {docId}
                </p>
              )}
              {signingResult.isCompleted ? (
                <p className="text-sm text-app-ink-muted">
                  {t("completion-confirmation", {
                    defaultValue: "Recibirás una copia del documento firmado por correo electrónico."
                  })}
                </p>
              ) : (
                <p className="text-sm text-app-ink-muted">
                  {remainingSigners > 0
                    ? t("verification.waiting-remaining-signers", {
                        defaultValue:
                          "Faltan {{count}} firmante(s) para completar el documento.",
                        count: remainingSigners
                      })
                    : t("verification.pending-other-signers-message", {
                        defaultValue:
                          "Tu firma fue registrada. Este documento se completará cuando los demás firmantes finalicen."
                      })}
                </p>
              )}
            </div>

            {signingResult.isCompleted ? (
              <div className="w-full space-y-2">
                <Button onClick={handleDownloadSigned} disabled={!signedDownloadUrl} className="w-full">
                  {t("new-ui.individual-signing.download-signed", {
                    defaultValue: "Download signed document"
                  })}
                </Button>
                <Button
                  variant="secondary"
                  onClick={handleDownloadCertificate}
                  disabled={isPreparingCertificate}
                  className="w-full"
                >
                  {isPreparingCertificate
                    ? t("generating-certificate", { defaultValue: "Generating certificate" })
                    : certificateDownloadUrl
                      ? t("new-ui.individual-signing.download-certificate", {
                          defaultValue: "Download certificate"
                        })
                      : t("new-ui.individual-signing.generate-certificate", {
                          defaultValue: "Generate certificate"
                        })}
                </Button>
              </div>
            ) : null}

            {/* Powered by DANAsign */}
            <div className="pt-4 border-t border-app-line w-full">
              <a 
                href="https://danasign.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 text-sm text-app-ink-muted hover:text-app-brand transition-colors"
              >
                <span>{t("powered-by", { defaultValue: "Powered by" })}</span>
                <span className="font-semibold">DANAsign</span>
              </a>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-app-sand">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-app-line bg-white shadow-sm">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-3 py-2 md:px-4 md:py-3">
          <div className="flex items-center gap-2 md:gap-4 min-w-0">
            {tenantLogo && (
              <img 
                src={tenantLogo} 
                alt="Logo" 
                className="h-6 md:h-8 w-auto object-contain flex-shrink-0"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            )}
            <div className="hidden sm:block h-8 w-px bg-app-line"></div>
            <h1 className="text-sm md:text-lg font-semibold text-app-ink truncate">
              {documentName || t("document", { defaultValue: "Documento" })}
            </h1>
          </div>
          <div className="flex items-center gap-1 md:gap-2 flex-shrink-0">
            {/* Mobile sidebar toggle */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              className="md:hidden"
              aria-label={t("toggle-sidebar", { defaultValue: "Ver información" })}
            >
              <Icon name="menu" className="h-5 w-5" />
            </Button>
            <Button variant="ghost" size="sm" onClick={handleZoomOut} disabled={zoomScale <= MIN_ZOOM}>
              <Icon name="minus" className="h-4 w-4" />
            </Button>
            <span className="hidden sm:inline min-w-[60px] text-center text-sm text-app-ink-muted">
              {Math.round(zoomScale * 100)}%
            </span>
            <Button variant="ghost" size="sm" onClick={handleZoomIn} disabled={zoomScale >= MAX_ZOOM}>
              <Icon name="plus" className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Main content */}
      <div className="flex flex-1 overflow-hidden pb-28 md:pb-16">
        {/* PDF Viewer */}
        <div className="flex-1 overflow-auto p-2 md:p-4" ref={containerRef} data-tut="tour-pdf-area">
          <div className="mx-auto relative" style={{ width: `${pageWidth * zoomScale}px` }}>
            {/* Envelope ID header */}
            {docId && (
              <div className="text-center py-1 mb-1 text-[10px] text-gray-400 font-mono">
                Envelope ID: {docId}
              </div>
            )}
            {pdfFile ? (
              <>
                <Document
                  file={pdfFile}
                  onLoadSuccess={({ numPages: pages }) => setNumPages(pages)}
                  loading={
                    <div className="flex items-center justify-center py-20">
                      <div className="h-8 w-8 animate-spin rounded-full border-4 border-app-brand border-t-transparent"></div>
                    </div>
                  }
                >
                  <Page
                    pageNumber={currentPageNumber}
                    width={pageWidth}
                    scale={zoomScale}
                    renderTextLayer={false}
                    renderAnnotationLayer={false}
                    onLoadSuccess={handlePageLoadSuccess}
                  />
                </Document>
                
                {/* Render interactive widgets for current page */}
                {(() => {
                  console.log("=== WIDGET RENDERING DEBUG ===");
                  console.log("myPlaceholders:", myPlaceholders);
                  console.log("currentPageNumber:", currentPageNumber);
                  
                  const currentPagePlaceholders = myPlaceholders.filter(
                    (pageData) => Number(pageData.pageNumber) === currentPageNumber
                  );
                  const currentPageOtherCompleted = otherCompletedPlaceholders.filter(
                    (pageData) => Number(pageData.pageNumber) === currentPageNumber
                  );

                  const currentOriginalWidth = pageOriginalWidths[currentPageNumber];
                  const currentOriginalHeight = pageOriginalHeights[currentPageNumber];
                  const renderedPageHeight =
                    currentOriginalWidth > 0 && currentOriginalHeight > 0
                      ? pageWidth * (currentOriginalHeight / currentOriginalWidth)
                      : pageWidth * 1.4142; // A4 fallback ratio
                  
                  console.log("currentPagePlaceholders:", currentPagePlaceholders);
                  
                  const editableAndPrefillWidgets = currentPagePlaceholders.flatMap((pageData) => {
                    const positions = pageData.pos || [];
                    console.log(`Page ${pageData.pageNumber} positions:`, positions);

                    return positions.map((widget, widgetIndex) => {
                      const widgetId = `${pageData.pageNumber}-${widgetIndex}`;
                      const widgetStateData = getWidget(widgetId);
                      const isEditableWidget = canCurrentSignerEditWidget(
                        widget,
                        widget?.__holderMeta || pageData
                      );

                      console.log(`Rendering widget ${widgetId}:`, {
                        widget,
                        widgetStateData,
                        pageWidth,
                        pageOriginalWidth: pageOriginalWidths[currentPageNumber],
                        zoomScale,
                        isEditableWidget
                      });

                      return (
                        <InteractiveWidget
                          key={widgetId}
                          widgetId={widgetId}
                          widget={widget}
                          pageWidth={pageWidth}
                          pageHeight={renderedPageHeight}
                          pageOriginalWidth={pageOriginalWidths[currentPageNumber]}
                          pageOriginalHeight={pageOriginalHeights[currentPageNumber]}
                          zoomScale={zoomScale}
                          widgetState={widgetStateData}
                          onWidgetClick={handleWidgetClick}
                          isActive={activeWidgetId === widgetId}
                          isReadOnly={!isEditableWidget}
                        />
                      );
                    });
                  });

                  const completedOtherSignerWidgets = currentPageOtherCompleted.flatMap((pageData) => {
                    const positions = pageData.pos || [];
                    return positions.map((widget, widgetIndex) => {
                      const widgetId = `readonly-${pageData.pageNumber}-${widgetIndex}`;
                      const responseValue = widget?.options?.response;
                      const readOnlyWidgetState = {
                        value: responseValue,
                        completed:
                          responseValue !== null &&
                          responseValue !== undefined &&
                          (typeof responseValue !== "string" || responseValue.trim() !== ""),
                        type: widget?.type || "text",
                        required: false,
                        interactive: false,
                        isPrefill: false
                      };

                      return (
                        <InteractiveWidget
                          key={widgetId}
                          widgetId={widgetId}
                          widget={widget}
                          pageWidth={pageWidth}
                          pageHeight={renderedPageHeight}
                          pageOriginalWidth={pageOriginalWidths[currentPageNumber]}
                          pageOriginalHeight={pageOriginalHeights[currentPageNumber]}
                          zoomScale={zoomScale}
                          widgetState={readOnlyWidgetState}
                          isActive={false}
                          isReadOnly
                        />
                      );
                    });
                  });

                  return [...completedOtherSignerWidgets, ...editableAndPrefillWidgets];
                })()}
              </>
            ) : (
              <div className="flex items-center justify-center rounded-lg border-2 border-dashed border-app-line bg-white py-20">
                <p className="text-sm text-app-ink-muted">
                  {t("no-document-loaded", { defaultValue: "No document loaded" })}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Sidebar - Desktop: static, Mobile: slide-out drawer */}
        {isSidebarOpen && (
          <div
            className="fixed inset-0 z-30 bg-black/30 md:hidden"
            onClick={() => setIsSidebarOpen(false)}
          />
        )}
        <aside
          className={`
            fixed top-0 right-0 z-40 h-full w-72 border-l border-app-line bg-white p-4
            transform transition-transform duration-200 ease-in-out
            md:static md:z-auto md:w-80 md:translate-x-0 md:transition-none
            ${isSidebarOpen ? 'translate-x-0' : 'translate-x-full'}
          `}
        >
          {/* Mobile close button */}
          <div className="flex items-center justify-between mb-3 md:hidden">
            <span className="text-sm font-semibold text-app-ink">
              {t("info", { defaultValue: "Información" })}
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsSidebarOpen(false)}
              aria-label={t("close", { defaultValue: "Cerrar" })}
            >
              <Icon name="x" className="h-5 w-5" />
            </Button>
          </div>
          <div data-tut="tour-signer-info">
            <Card>
            <h2 className="text-sm font-semibold uppercase tracking-wider text-app-ink-muted">
              {t("signer-info", { defaultValue: "Signer Information" })}
            </h2>
            <div className="mt-3 space-y-2">
              <div>
                <p className="text-xs text-app-ink-muted">{t("name", { defaultValue: "Name" })}</p>
                <p className="text-sm font-medium text-app-ink">{signerName || "—"}</p>
              </div>
              <div>
                <p className="text-xs text-app-ink-muted">{t("email", { defaultValue: "Email" })}</p>
                <p className="text-sm font-medium text-app-ink">{signerEmail || "—"}</p>
              </div>
            </div>
            </Card>
          </div>

          <div data-tut="tour-fields-progress">
            <Card className="mt-4">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-app-ink-muted">
              {t("fields-to-sign", { defaultValue: "Fields to Sign" })}
            </h2>
            <p className="mt-2 text-sm text-app-ink-muted">
              {totalFieldsCount > 0
                ? t("fields-count", {
                    defaultValue: "{{count}} field(s) require your signature",
                    count: totalFieldsCount
                  })
                : t("no-fields", { defaultValue: "No fields to sign" })}
            </p>
            
            {/* Progress indicator */}
            {totalFieldsCount > 0 && (
              <div className="mt-4">
                <div className="flex items-center justify-between text-xs text-app-ink-muted mb-1">
                  <span>{t("progress", { defaultValue: "Progress" })}</span>
                  <span>{getProgress()}%</span>
                </div>
                <div className="h-2 w-full rounded-full bg-gray-200">
                  <div
                    className="h-2 rounded-full bg-green-500 transition-all duration-300"
                    style={{ width: `${getProgress()}%` }}
                  />
                </div>
              </div>
            )}
            </Card>
          </div>
        </aside>
      </div>

      {/* Footer with navigation - Fixed at bottom */}
      <footer className="fixed bottom-0 left-0 right-0 z-40 border-t border-app-line bg-white shadow-lg">
        <div className="mx-auto max-w-7xl px-3 py-2 md:px-4 md:py-3">
          {/* Desktop: single row */}
          <div className="hidden md:flex items-center justify-between">
            {/* Powered by DANAsign - Left */}
            <a 
              href="https://danasign.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-xs text-app-ink-muted hover:text-app-brand transition-colors flex items-center gap-1"
            >
              <span>{t("powered-by", { defaultValue: "Powered by" })}</span>
              <span className="font-semibold">DANAsign</span>
            </a>
            
            {/* Navigation controls - Center */}
            <div className="flex items-center gap-2" data-tut="tour-navigation-controls">
              <Button
                variant="ghost"
                onClick={handleFirstPage}
                disabled={currentPageNumber <= 1}
                className="min-w-[40px]"
                title={t("first-page", { defaultValue: "Primera página" })}
              >
                <Icon name="pageFirst" className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                onClick={handlePreviousPage}
                disabled={currentPageNumber <= 1}
                className="min-w-[40px]"
                title={t("previous", { defaultValue: "Anterior" })}
                aria-label={t("previous", { defaultValue: "Anterior" })}
              >
                <Icon name="chevronLeft" className="h-4 w-4" />
              </Button>
              <span className="text-sm font-medium text-app-ink">
                {t("page-of", {
                  defaultValue: "Página {{current}} de {{total}}",
                  current: currentPageNumber,
                  total: numPages
                })}
              </span>
              <Button
                variant="ghost"
                onClick={handleNextPage}
                disabled={currentPageNumber >= numPages}
                className="min-w-[100px]"
              >
                {t("next", { defaultValue: "Siguiente" })}
                <Icon name="chevronRight" className="h-4 w-4 ml-1" />
              </Button>
              <Button
                variant="ghost"
                onClick={handleLastPage}
                disabled={currentPageNumber >= numPages}
                className="min-w-[40px]"
                title={t("last-page", { defaultValue: "Última página" })}
              >
                <Icon name="pageLast" className="h-4 w-4" />
              </Button>
            </div>
            
            {/* Complete / Decline actions - Right */}
            <div className="flex items-center gap-2" data-tut="tour-submit-actions">
              <Button
                variant="danger"
                onClick={handleDeclineDocument}
                disabled={isSubmitting || alreadySigned}
                className="min-w-[110px]"
              >
                {t("decline", { defaultValue: "Declinar" })}
              </Button>
              <Button
                onClick={handleCompleteDocument}
                disabled={!isAllRequiredFilled() || isSubmitting || alreadySigned}
                className="min-w-[140px]"
              >
                {isSubmitting ? (
                  <>
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent mr-2"></div>
                    {t("submitting", { defaultValue: "Enviando..." })}
                  </>
                ) : (
                  t("complete-document", { defaultValue: "Completar documento" })
                )}
              </Button>
            </div>
          </div>

          {/* Mobile: two rows */}
          <div className="flex flex-col gap-2 md:hidden">
            {/* Row 1: Page navigation */}
            <div className="flex items-center justify-center gap-1" data-tut="tour-navigation-controls">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleFirstPage}
                disabled={currentPageNumber <= 1}
              >
                <Icon name="pageFirst" className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handlePreviousPage}
                disabled={currentPageNumber <= 1}
                aria-label={t("previous", { defaultValue: "Anterior" })}
              >
                <Icon name="chevronLeft" className="h-4 w-4" />
              </Button>
              <span className="text-xs font-medium text-app-ink mx-1">
                {currentPageNumber} / {numPages}
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleNextPage}
                disabled={currentPageNumber >= numPages}
              >
                <Icon name="chevronRight" className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLastPage}
                disabled={currentPageNumber >= numPages}
              >
                <Icon name="pageLast" className="h-4 w-4" />
              </Button>
            </div>
            {/* Row 2: Actions */}
            <div className="flex items-center gap-2" data-tut="tour-submit-actions">
              <Button
                variant="danger"
                size="sm"
                onClick={handleDeclineDocument}
                disabled={isSubmitting || alreadySigned}
                className="flex-1"
              >
                {t("decline", { defaultValue: "Declinar" })}
              </Button>
              <Button
                size="sm"
                onClick={handleCompleteDocument}
                disabled={!isAllRequiredFilled() || isSubmitting || alreadySigned}
                className="flex-1"
              >
                {isSubmitting ? (
                  <>
                    <div className="h-3 w-3 animate-spin rounded-full border-2 border-white border-t-transparent mr-1"></div>
                    {t("submitting", { defaultValue: "Enviando..." })}
                  </>
                ) : (
                  t("complete-document", { defaultValue: "Completar" })
                )}
              </Button>
            </div>
          </div>
        </div>
      </footer>

      {/* Tour removed */}

      {/* Signature Modal */}
      <SignatureModal
        isOpen={isSignatureModalOpen}
        onClose={() => {
          setIsSignatureModalOpen(false);
          setApplyInitialsToAllWidgets(false);
          setActiveWidgetId(null);
        }}
        onSave={handleSignatureSave}
        widgetType={activeWidgetType}
        applyToAllInitials={applyInitialsToAllWidgets}
        onApplyToAllInitialsChange={setApplyInitialsToAllWidgets}
      />

      {/* Text Input Modal */}
      <Modal
        isOpen={isTextModalOpen}
        onClose={() => {
          setIsTextModalOpen(false);
          setTextInputValue('');
          setActiveWidgetId(null);
        }}
        title={t(`widget-${activeWidgetType}`, { defaultValue: activeWidgetType })}
      >
        <div className="space-y-4">
          <div>
            <label htmlFor="text-input" className="block text-sm font-medium text-gray-700 mb-2">
              {t('enter-value', { defaultValue: 'Ingrese el valor' })}
            </label>
            <input
              id="text-input"
              type={activeWidgetType === 'email' ? 'email' : 'text'}
              value={textInputValue}
              onChange={(e) => setTextInputValue(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              placeholder={t(`placeholder-${activeWidgetType}`, { 
                defaultValue: activeWidgetType === 'name' ? 'Nombre completo' :
                             activeWidgetType === 'email' ? 'correo@ejemplo.com' :
                             activeWidgetType === 'job title' ? 'Cargo' :
                             activeWidgetType === 'company' ? 'Nombre de la empresa' :
                             'Ingrese texto'
              })}
              autoFocus
            />
          </div>
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button
              variant="ghost"
              onClick={() => {
                setIsTextModalOpen(false);
                setTextInputValue('');
                setActiveWidgetId(null);
              }}
            >
              {t('cancel', { defaultValue: 'Cancelar' })}
            </Button>
            <Button
              onClick={handleTextSave}
              disabled={!textInputValue.trim()}
            >
              {t('save', { defaultValue: 'Guardar' })}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Checkbox Modal */}
      <Modal
        isOpen={isCheckboxModalOpen}
        onClose={() => {
          setIsCheckboxModalOpen(false);
          setCheckboxValue(false);
          setActiveWidgetId(null);
        }}
        title={t('checkbox', { defaultValue: 'Casilla de verificación' })}
      >
        <div className="space-y-4">
          <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
            <input
              type="checkbox"
              id="checkbox-input"
              checked={checkboxValue}
              onChange={(e) => setCheckboxValue(e.target.checked)}
              className="mt-1 h-5 w-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <label htmlFor="checkbox-input" className="flex-1 text-sm text-gray-700">
              {checkboxLabel}
            </label>
          </div>
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button
              variant="ghost"
              onClick={() => {
                setIsCheckboxModalOpen(false);
                setCheckboxValue(false);
                setActiveWidgetId(null);
              }}
            >
              {t('cancel', { defaultValue: 'Cancelar' })}
            </Button>
            <Button onClick={handleCheckboxSave}>
              {t('save', { defaultValue: 'Guardar' })}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Date Modal */}
      <Modal
        isOpen={isDateModalOpen}
        onClose={() => {
          setIsDateModalOpen(false);
          setDateValue('');
          setActiveWidgetId(null);
        }}
        title={t('date', { defaultValue: 'Fecha' })}
      >
        <div className="space-y-4">
          <div>
            <label htmlFor="date-input" className="block text-sm font-medium text-gray-700 mb-2">
              {t('select-date', { defaultValue: 'Seleccione una fecha' })}
            </label>
            <input
              id="date-input"
              type="date"
              value={dateValue}
              onChange={(e) => setDateValue(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              autoFocus
            />
          </div>
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button
              variant="ghost"
              onClick={() => {
                setIsDateModalOpen(false);
                setDateValue('');
                setActiveWidgetId(null);
              }}
            >
              {t('cancel', { defaultValue: 'Cancelar' })}
            </Button>
            <Button
              onClick={handleDateSave}
              disabled={!dateValue}
            >
              {t('save', { defaultValue: 'Guardar' })}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Image Upload Modal */}
      <Modal
        isOpen={isImageModalOpen}
        onClose={() => {
          setIsImageModalOpen(false);
          setImagePreview('');
          setImageFile(null);
          setActiveWidgetId(null);
        }}
        title={t(`widget-${activeWidgetType}`, { defaultValue: activeWidgetType })}
      >
        <div className="space-y-4">
          <div>
            <label htmlFor="image-input" className="block text-sm font-medium text-gray-700 mb-2">
              {t('upload-image', { defaultValue: 'Cargar imagen' })}
            </label>
            <input
              id="image-input"
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
            />
            <p className="mt-1 text-xs text-gray-500">
              {t('image-requirements', { defaultValue: 'PNG, JPG, JPEG (máx. 5MB)' })}
            </p>
          </div>

          {isImageLoading && (
            <div className="mt-4 flex flex-col items-center justify-center py-8">
              <div className="animate-spin rounded-full h-10 w-10 border-4 border-gray-200 border-t-blue-600"></div>
              <p className="mt-3 text-sm text-gray-500">
                {t('loading-image', { defaultValue: 'Cargando imagen...' })}
              </p>
            </div>
          )}
          
          {imagePreview && !isImageLoading && (
            <div className="mt-4">
              <p className="text-sm font-medium text-gray-700 mb-2">
                {t('preview', { defaultValue: 'Vista previa' })}
              </p>
              <div className="border border-gray-300 rounded-lg p-4 bg-gray-50 flex items-center justify-center">
                <img
                  src={imagePreview}
                  alt="Preview"
                  className="max-h-48 max-w-full object-contain"
                />
              </div>
            </div>
          )}
          
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button
              variant="ghost"
              onClick={() => {
                setIsImageModalOpen(false);
                setImagePreview('');
                setImageFile(null);
                setActiveWidgetId(null);
              }}
            >
              {t('cancel', { defaultValue: 'Cancelar' })}
            </Button>
            <Button
              onClick={handleImageSave}
              disabled={!imagePreview || isImageLoading}
            >
              {t('save', { defaultValue: 'Guardar' })}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Verification Modal */}
      <VerificationModal
        isOpen={showVerificationModal}
        onClose={handleVerificationClose}
        onSuccess={handleVerificationSuccess}
        signer={{
          name: signerName,
          email: signerEmail,
          objectId: signerObjectId
        }}
        documentId={docId}
        verificationConfig={verificationConfig}
      />

      {/* Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showConfirmDialog}
        title={t("confirm-submission", { defaultValue: "Confirmar envío" })}
        message={t("confirm-submission-message", { 
          defaultValue: "¿Está seguro de que desea completar y enviar este documento? Esta acción no se puede deshacer." 
        })}
        confirmText={t("confirm", { defaultValue: "Confirmar" })}
        cancelText={t("cancel", { defaultValue: "Cancelar" })}
        onConfirm={handleConfirmSubmit}
        onCancel={() => setShowConfirmDialog(false)}
        type="warning"
      />

      <Modal
        isOpen={showDeclineConfirmDialog}
        onClose={() => {
          setShowDeclineConfirmDialog(false);
          setDeclineReason("");
        }}
        title={t("decline", { defaultValue: "Declinar" })}
      >
        <div className="space-y-4">
          <p className="text-sm text-app-ink-muted">
            {t("decline-confirm-message", {
              defaultValue: "¿Está seguro de que desea declinar este documento?"
            })}
          </p>
          <div>
            <label htmlFor="decline-reason" className="mb-1 block text-sm font-medium text-app-ink">
              {t("reason", { defaultValue: "Razón" })}
            </label>
            <textarea
              id="decline-reason"
              value={declineReason}
              onChange={(event) => setDeclineReason(event.target.value)}
              maxLength={500}
              rows={4}
              className="w-full rounded-lg border border-app-line px-3 py-2 text-sm text-app-ink focus:border-app-brand focus:outline-none focus:ring-2 focus:ring-app-brand/20"
              placeholder={t("new-ui.recipient.decline-reason-placeholder", {
                defaultValue: "Escriba una razón (opcional)"
              })}
            />
            <p className="mt-1 text-right text-xs text-app-ink-muted">{declineReason.length}/500</p>
          </div>
          <div className="flex justify-end gap-2 border-t border-app-line pt-4">
            <Button
              variant="ghost"
              onClick={() => {
                setShowDeclineConfirmDialog(false);
                setDeclineReason("");
              }}
            >
              {t("cancel", { defaultValue: "Cancelar" })}
            </Button>
            <Button variant="danger" onClick={handleConfirmDecline}>
              {t("decline", { defaultValue: "Declinar" })}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Alert */}
      {alert.show && (
        <Alert
          type={alert.type}
          message={alert.message}
          onClose={() => setAlert({ show: false, type: 'error', message: '' })}
          duration={alert.type === 'success' ? 3000 : 5000}
        />
      )}

      {/* Global submit overlay */}
      {isSubmitting && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-slate-900/35 backdrop-blur-[1px]">
          <div className="flex flex-col items-center gap-3 rounded-xl bg-white px-6 py-5 shadow-2xl">
            <div className="h-10 w-10 animate-spin rounded-full border-4 border-app-brand border-t-transparent" />
            <p className="text-sm font-medium text-app-ink">
              {t("submitting", { defaultValue: "Enviando..." })}
            </p>
            <p className="text-xs text-app-ink-muted">
              {t("processing-signature", { defaultValue: "Procesando firma..." })}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

export default ExternalSignerPage;
